import { makeAutoObservable, reaction, runInAction } from 'mobx';
import i18next from 'i18next';

import { analytics, dataLayerActions } from '@/app/parts/Analytics';

import { getBetStatus, place, placeBetBuilder, placeExpress } from '@/lib/api/auth';
import { GetMaxBet, getPresetSum } from '@/lib/api/bets';
import { getPlayerBonuses } from '@/lib/api/bonuses';
import { logger } from '@/lib/api/logger';
import { sendMsg } from '@/lib/api/postMessageAPI';
import {
  getBetBuilderRecommendations,
  getExpressRecommendationTips,
  getExpressRecommendations,
} from '@/lib/api/recommendations';
import { Socket } from '@/lib/api/Socket';
import { mobx } from '@/lib/mobx';
import { betStatus } from '@/lib/statuses';
import { asyncCooldown } from '@/helpers/cooldown';
import { debounceAsync } from '@/helpers/debounce';
import { difference } from '@/helpers/difference';
import { extractEventAndMarketIds } from '@/helpers/extractEventAndMarketIds';
import { fromArrToList } from '@/helpers/fromArrToList';
import { getDateByInterval } from '@/helpers/getDateByInterval';
import { getFromWebSocket } from '@/helpers/getFromWebSocket';
import { getHashFromString } from '@/helpers/getHashFromString';
import { getTimeFromStart } from '@/helpers/getTimeFromStart';
import { isEmpty } from '@/helpers/isEmpty';
import { LS } from '@/helpers/localStorageHelper';
import { normalizeBetForLocalStorage } from '@/helpers/normalizeBetForLocalStorage';
import { taxes } from '@/helpers/oddsConverter/taxes';
import { prepareShareBetData } from '@/helpers/prepareShareBetData';
import {
  PLACE_BET_DEBOUNCE_TIME,
  betTrackMark,
  expressMaxOdd,
  liveFullLine,
  prematchFullLine,
} from '@/config/constants';

import { Bet } from './Bet.mobx';

function timeout(s) {
  return new Promise((resolve) => setTimeout(resolve, s * 1000));
}

export class Betslip {
  /**
   * @param {import('../RootStoreMobx').RootStoreMobx} root
   */
  constructor(root, initialState) {
    makeAutoObservable(this);

    this.root = root;
    Object.assign(this, initialState);

    reaction(
      () => this.bets.length,
      (betsLength) => {
        const savedTab = LS.get('esBetslipTab') ?? null;
        if (!betsLength) {
          this.setCurrentTab('');
          LS.remove('esBetslipTab');
          this.setBlocksIdsMap({});
          this.resetExpressTips();
          return;
        }

        this.comboBlockingHandler();
        let tab = 'SINGLEBET';
        const hasBetBuilder = this.bets.some(({ isBetBuilder }) => isBetBuilder);
        const isRemovingBets = betsLength < this.prevBetsLength;
        const isCurrentlyInExpress = this.currentTab === 'EXPRESS';
        // Condition so we could handle deleting items from combo tab, without switching to singlebet tab
        if (isRemovingBets && isCurrentlyInExpress && betsLength > 1) {
          tab = 'EXPRESS';
          this.getExpressTips();
        } else if (
          betsLength > 1 &&
          !(this.duplicates.length || this.comboOutrights.length) &&
          !this.isBlockingCombo &&
          !hasBetBuilder
        ) {
          tab = this.root.shareBet?.isShareBetEnterCode
            ? (this.root.shareBet.shareBetSlipTab ?? 'EXPRESS')
            : this.prevBetsLength === 1
              ? 'EXPRESS'
              : (savedTab ?? 'EXPRESS');
          this.getExpressTips();
        }
        // handle bets for analytics
        if (betsLength >= this.prevBetsLength) {
          const currentTab = tab === 'EXPRESS' ? 'EXPRESS' : 'SINGLEBET';
          analytics.betSlipBets[currentTab] = this.bets.filter(
            // At this stage, there is no need to add “restored bets” to analytics
            // All necessary “bets” will be added there before a successful “place bet”
            ({ id }) => !analytics.restoredBetIds.has(id),
          );
          analytics.restoredBetIds.clear();
        }
        const esBetslipTab = LS.get('esBetslipTab') || null;
        if (tab === 'SINGLEBET' && tab !== esBetslipTab) {
          LS.set('esBetslipTab', 'SINGLEBET');
        }
        this.setCurrentTab(tab);
        this.prevBetsLength = betsLength;
        this.filterStorage();
      },
    );

    reaction(
      () => this.currentTab,
      () => {
        this.comboBlockingHandler();
        this.checkBetSlipWithBonus();
      },
    );

    reaction(
      () => this.betsWithBonus,
      () => {
        this.checkBetSlipWithBonus();
      },
    );

    reaction(
      () => this.quickBetsResolveCounter,
      (counter) => {
        if (!counter && this.quickBetsSuccessIds.length) {
          //Updating Balance on successfull quick bets
          const { profile } = this.root;
          profile.updateBalance();
          this.quickBetsSuccessIds = [];
        }
      },
    );

    reaction(
      () => this.root.store.activeEvent,
      () => {
        this.setLiveBet({});
      },
    );
  }

  prevBetsLength = 0;

  /**
   * Bets you just placed - awaiting to restore
   * @property {array}
   */
  ticketsToRestore = [];

  /**
   * Bets in betslip
   * @property {array}
   */
  bets = [];

  /**
   * express tips in betslip
   * @property {array}
   */
  expressTips = [];

  /**
   * Bets for 'popular bets widget'
   * @property {array}
   */
  popularBets = [];

  /**
   * express tips from fetch
   * @property {array}
   */
  expressTipsFromFetch = [];

  /**
   * has more express tips in storage
   * @property {boolean}
   */
  hasExpressTipsInStorage = false;

  /**
   * has more express tips in storage
   * @property {boolean}
   */
  hasExpressTipsOnServer = true;

  /**
   * express tips that was showed to user
   * @property {array}
   */
  expressTipsShowed = [];

  /**
   * express tips currentPage
   * @property {Number}
   */
  expressTipsCurrentPage = 1;

  /**
   * Betids with prices when they was added
   * @property {array} { id: string, price: number }[]
   */
  initialBets = [];

  /**
   * Bets in live betslip
   * @property {Set}
   */
  liveBet = {};

  pendingBets = [];

  betStatuses = [];

  inProgress = false;

  isExpressPresetsInProgress = false;

  statusRespondsLength = 0;

  failedStatusesLength = 0;

  successStatusesLength = 0;

  isPendingBets = false;

  isPendingBetsFinished = false;

  expressPresetsLoadingId = '';

  notices = {
    comboOutrights: false,
    duplicates: false,
  };

  isDisabledPlacebetButton = false;

  currentTab = '';

  acceptChanges = true;

  expressAmount = Number(LS.get('defaultStake')) || null;

  maxDelay = null;

  betId = '';

  quickBetsPresetSum = [10, 20, 50, 100];

  presetSumData = {
    quickBetsPreset: this.quickBetsPresetSum,
  };

  requestCount = 0;

  bonuses = [];

  allBonuses = [];

  bonusesTimestamp = null;

  bonusBetInProgressId = null;

  defaultStake = Number(LS.get('defaultStake')) || null;

  isShowComboBooster = false;

  isBlockingCombo = false;

  isShowAlertBlockingCombo = false;

  activeBetslipInput = {};

  isBetslipKeyboardPressed = false;

  isQuickBetsMode = false;

  isQuickBetsActive = false;

  /** @type {number | string | null} */
  quickBetsSum = this.defaultStake;

  quickBetSuccess = false;

  isQuickBetsInputError = false;

  quickBetsResolveCounter = 0;

  quickBetsSuccessIds = [];

  expressPresets = {};

  expressPresetsByTournament = {};

  isUpdateExpressPreset = false;

  betItemWithMaxBet = false;

  isExpressPresetBetSlip = false;

  isBetSlipWithBonuses = false;

  isClearBets = false;

  isBetslipSlots = false;

  minBoostOdd = 1.4;

  comboBoostDefaultLevel = {
    num: 0,
    odd: 1.0,
  };

  forbiddenComboBoostDisciplines = [];

  comboBoostLevels = [];

  boostedOddsMaxLevel = this.comboBoostLevels.slice(-1)[0] || { num: 0 };

  boostedOddsMaxNum = this.boostedOddsMaxLevel.num;

  presetsMarketsIds = [];

  /**
   * Stores information about bets placed through
   * widgets like "PopularBets" and "ExpressPresets".
   */
  settledBets = {};

  get acceptChangesForBets() {
    return this.acceptChanges;
  }
  get hsOddsChanged() {
    if (this.acceptChanges) return false;
    return this.bets.some((bet) => {
      const item = this.initialBets.find(({ id }) => id === bet.id) ?? { price: 0 };
      return item.price !== bet.price;
    });
  }

  get betIdsFromBetSlip() {
    return this.bets.reduce((acc, { id }) => {
      acc[id] = true;
      return acc;
    }, {});
  }

  get marketIdsFromBetSlip() {
    return this.bets.reduce((acc, { pidLid = '' }) => {
      if (pidLid) {
        const [_, __, id] = pidLid.split('.');
        acc[id] = true;
      }
      return acc;
    }, {});
  }

  get betsFromBetSlipByIds() {
    return this.bets.reduce((acc, bet) => {
      acc[bet.id] = bet;
      return acc;
    }, {});
  }

  get hasExpressBonus() {
    const betsWithBonus = this.betsWithBonus;
    const [{ bonus } = {}] = betsWithBonus;
    const expressCoefficient = this.bets.reduce((acc, { price }) => acc * price, 1);

    return (
      betsWithBonus.length &&
      this.bets.length === betsWithBonus.length &&
      betsWithBonus.every(({ bonus: { id } }) => id === bonus.id) &&
      this.bets.every((bet) => betsWithBonus.find(({ bet: { id } }) => bet.id === id)) &&
      this.bets.length >= bonus.minSelection &&
      this.bets.length <= bonus.maxSelection &&
      expressCoefficient <= bonus.maxOdd &&
      expressCoefficient * bonus.betAmount - bonus.betAmount < bonus.currentMaxWin
    );
  }

  get betsWithBonus() {
    return this.bets.reduce((acc, bet) => {
      const bonus = this.bonuses.reduce((acc, bonus) => {
        const { price, eventId, tournamentId, disciplineId, isFrozen } = bet;
        if (isFrozen) {
          return acc;
        }

        const availableTournaments = bonus.availableTournaments ?? [];
        const isAllTournaments = availableTournaments.length === 0;
        const availableTournamentsList = fromArrToList(availableTournaments.map(({ id }) => id));
        const bonusDisciplinesList = fromArrToList(bonus.disciplines);

        if (!(isAllTournaments || availableTournamentsList[tournamentId])) {
          return acc;
        }

        if (
          price >= bonus.minOdd &&
          price <= bonus.maxOdd &&
          bonusDisciplinesList[disciplineId] &&
          bonus.stage.some((i) => {
            if (i === '2') {
              return eventId.startsWith(i) || eventId.startsWith('5');
            }
            if (i === '1') {
              return eventId.startsWith(i) || eventId.startsWith('4');
            }

            return false;
          })
        ) {
          if (acc && acc.endingDate < bonus.endingDate) {
            return acc;
          }

          return bonus;
        }
        return acc;
      }, undefined);
      return bonus ? [...acc, { bet, bonus }] : acc;
    }, []);
  }

  get isWithFreeBets() {
    if (this.currentTab === 'EXPRESS') {
      return this.isBetSlipWithBonuses;
    }

    if (this.currentTab === 'SINGLEBET') {
      return (
        this.isBetSlipWithBonuses &&
        !this.betsWithBonus.some(({ bonus: { minSelection } }) => minSelection > 1)
      );
    }
    return false;
  }

  /**
   * Get total stake of all single bets
   * @returns {number}
   */
  get totalStake() {
    const { roundCurrency } = this.root.profile;

    const totalStake = this.bets.reduce((acc, betsBet) => {
      let { stake = '0', id, pidLid } = betsBet;
      const bet = this.root.store.getOutcomeByLID(id, pidLid);

      if (bet?.isFrozen || !stake) {
        stake = 0;
      }

      return acc + parseFloat(stake);
    }, 0);

    return roundCurrency(totalStake);
  }

  /**
   * Get single bets taxes
   * @returns {Object}
   */
  get singleBetsTaxes() {
    const { hasTaxes } = this.root.ui;
    if (!hasTaxes) return {};

    const { roundCurrency } = this.root.profile;
    const { brand } = this.root.auth;
    const brandTaxes = taxes[brand];

    if (!brandTaxes) {
      throw new Error(`Expected brand taxes to exist for: ${brand}`);
    }

    return this.bets.reduce(
      (acc, item) => {
        let { stake = 0, price } = item;
        const { id = '', pidLid = '' } = item;
        const bet = this.root.store.getOutcomeByLID(id, pidLid);
        if (!bet || bet.isFrozen) {
          stake = 0;
        } else {
          price = bet.price;
        }

        const betTaxes = brandTaxes.getTaxes(stake, price || 0, roundCurrency);

        Object.entries(betTaxes).forEach(([k, v]) => {
          acc[k] = roundCurrency(+acc[k] + Number(v));
        });

        return acc;
      },
      {
        incomeTax: 0,
        possibleWin: 0,
        stakeAfterVat: 0,
        vatAmount: 0,
      },
    );
  }

  /**
   * Get possible win
   * @returns {number}
   */
  get possibleWin() {
    const { roundCurrency } = this.root.profile;
    const { hasTaxes } = this.root.ui;
    const { brand } = this.root.auth;

    const possibleWin = this.bets.reduce((acc, item) => {
      const { id, pidLid, isBetBuilder = false, coefficient } = item;
      let { stake = 0, price } = item;
      const bet = this.root.store.getOutcomeByLID(id, pidLid);

      if ((!bet || bet.isFrozen) && !isBetBuilder) {
        if (!bet || bet.isFrozen) {
          stake = 0;
        } else {
          price = bet.price;
        }
      }

      let betPossibleWin = stake * (isBetBuilder ? coefficient : price) || 0;

      if (hasTaxes && taxes[brand]) {
        const priceForTaxes = isBetBuilder ? coefficient : price || 0;
        const betTaxes = taxes[brand].getTaxes(stake, priceForTaxes, roundCurrency);

        betPossibleWin = Number(betTaxes.possibleWin);
      }

      return acc + betPossibleWin;
    }, 0);

    if (!possibleWin) {
      return 0;
    }

    return roundCurrency(possibleWin);
  }

  /**
   * Get bets with same events
   * @returns {array}
   */
  get duplicates() {
    const { duplicatedBets } = this.bets.reduce(
      (acc, { eventId }) => {
        if (!acc.singleBets.includes(eventId)) {
          acc.singleBets = [...acc.singleBets, eventId];
        } else if (!acc.duplicatedBets.includes(eventId)) {
          acc.duplicatedBets = [...acc.duplicatedBets, eventId];
        }
        return acc;
      },
      { duplicatedBets: [], singleBets: [] },
    );
    return duplicatedBets;
  }

  /**
   * Get bets with same events
   * @returns {array}
   */
  get comboOutrights() {
    return this.bets.reduce((acc, curr) => {
      const { eventId } = curr;
      const event = this.root.store.events.get(eventId);
      if (event && `${event.type}` === '1') {
        acc.push(eventId);
      }
      return acc;
    }, []);
  }

  /**
   * Get odds that >= minBoostOdd (1.4) coefficient for express
   * @returns {array}
   */
  getBoostedOdds(bets = this.bets, minBoostOdd = this.minBoostOdd) {
    return bets.filter((bet) => bet.price >= minBoostOdd);
  }

  get boostedOdds() {
    return this.getBoostedOdds();
  }

  /**
   * Get current, future boost levels
   * and boosted odds length
   * given the maximum number odds of combo booster
   * @returns {object}
   */
  getBoostLevels(
    boostedOdds = this.boostedOdds,
    boostedOddsMaxNum = this.boostedOddsMaxNum,
    comboBoostLevels = this.comboBoostLevels,
    boostedOddsMaxLevel = this.boostedOddsMaxLevel,
  ) {
    let boostedOddsLength = boostedOdds.length;
    boostedOddsLength =
      boostedOddsLength > boostedOddsMaxNum ? boostedOddsMaxNum : boostedOddsLength;
    const futureBoost =
      comboBoostLevels.find((level) => boostedOddsLength < level.num) || boostedOddsMaxLevel;
    const currentBoost =
      boostedOddsLength >= boostedOddsMaxNum
        ? boostedOddsMaxLevel
        : comboBoostLevels[comboBoostLevels.indexOf(futureBoost) - 1];
    return { boostedOddsLength, currentBoost, futureBoost };
  }

  get boostLevels() {
    return this.getBoostLevels();
  }

  /**
   * Is it enough odds in express for min boost level
   * @returns {boolean}
   */
  getIsComboBoosted({
    comboBoostLevels = this.comboBoostLevels,
    boostedOdds = this.boostedOdds,
    isShowComboBooster,
  }) {
    const minBoostItemsNum = comboBoostLevels.find((level) => level.minBoostLevel)?.num || 0;
    return boostedOdds.length >= minBoostItemsNum && isShowComboBooster;
  }

  get isComboBoosted() {
    const hasIgnoredDiscipline = this.bets.some(({ disciplineId = '' }) =>
      this.forbiddenComboBoostDisciplines.includes(disciplineId),
    );

    return (
      !hasIgnoredDiscipline &&
      this.getIsComboBoosted({
        isShowComboBooster: this.isShowComboBooster,
      })
    );
  }

  /**
   * Get total coefficient for express
   * @param {Array} bets - Array of (new Bet) items
   * @param {Object} boostLevels - Object of calculated combo booster levels
   * @param {Function} getOutcomeByLID - get outcome by LID
   * @param {Boolean} isShowComboBooster - is show combo booster
   * @returns {number}
   */
  getExpressOdd({
    bets = this.bets,
    boostLevels = this.boostLevels,
    getOutcomeByLID,
    isShowComboBooster,
  }) {
    let hasIgnoredDiscipline = false;
    const b = bets.reduce((acc, { price, id, pidLid, disciplineId = '' }) => {
      if (this.forbiddenComboBoostDisciplines.includes(disciplineId)) {
        hasIgnoredDiscipline = true;
      }

      const bet = getOutcomeByLID(id, pidLid);
      if (!bet || bet.isFrozen) {
        price = 1;
      } else {
        price = bet.price;
      }
      return acc * price;
    }, 1);
    const odd =
      isShowComboBooster && !hasIgnoredDiscipline ? b * (boostLevels.currentBoost.odd ?? 1) : b;
    return odd > expressMaxOdd ? expressMaxOdd : odd.toFixed(2);
  }

  get expressOdd() {
    const { getOutcomeByLID } = this.root.store;
    return this.getExpressOdd({
      getOutcomeByLID,
      isShowComboBooster: this.isShowComboBooster,
    });
  }

  /**
   * Get successful betsStatuses
   * @returns {array}
   */
  get successBets() {
    return this.betStatuses.filter(({ status }) => status === 2);
  }

  setLiveBet = (liveBet) => {
    this.liveBet = liveBet;
  };

  setComboBoosterSettings = (settings) => {
    if (!settings) {
      return;
    }

    const { ignoreDisciplines, rules, minCoefficient } = settings;

    this.comboBoostLevels = rules.reduce((acc, level, i) => {
      const currentLevel = {};
      if (i === 0) {
        acc.push(this.comboBoostDefaultLevel);
        currentLevel.minBoostLevel = true;
      }
      currentLevel.num = level.min;
      currentLevel.odd = level.booster;
      acc.push(currentLevel);

      return acc;
    }, []);

    this.minBoostOdd = minCoefficient;
    this.forbiddenComboBoostDisciplines = ignoreDisciplines;
    this.boostedOddsMaxLevel = this.comboBoostLevels.slice(-1)[0];
    this.boostedOddsMaxNum = this.boostedOddsMaxLevel.num;
  };

  setActiveBetslipInput = (input) => {
    this.activeBetslipInput = input;
  };

  setIsBetslipKeyboardPressed = (bool) => {
    this.isBetslipKeyboardPressed = bool;
  };

  setPopularBets = (popularBets) => {
    this.popularBets = popularBets;
  };

  resetExpressTips = () => {
    this.expressTips = [];
    this.hasExpressTipsOnServer = true;
  };

  /**
   * @description - update express tips to render
   * @param {Array<Object>} expressTipsData
   */
  updateExpressTips = async (expressTipsData) => {
    const tipsPids = expressTipsData.reduce((acc, { outcomeId, eventMarketId, eventId }) => {
      acc[outcomeId] = `1.${eventId}.${eventMarketId}`;
      return acc;
    }, {});

    await this.subscribeOnMarketsInSW({ marketsPids: tipsPids });

    this.expressTips = expressTipsData.reduce((acc, { outcomeId }) => {
      const pid = tipsPids[outcomeId];
      const outcome = this.root.store.getOutcomeByLID(outcomeId, pid);
      if (outcome) {
        let currentTip = this.expressTips.find(({ id }) => id === outcomeId);

        if (!currentTip) {
          currentTip = this.initNewBet({
            id: outcomeId,
            marketId: pid,
            trackMarks: betTrackMark.expressTip,
          });
        }

        acc.push(currentTip);
      }
      return acc;
    }, []);
  };

  /**
   * @description - get express tips from API
   * @param {Object} params
   * @param {Boolean} params.isRunTipsSkipping - tips items skip effect
   * @param {Number} params.currentPage - current page
   */
  getExpressTips = async (params) => {
    const isExpress = this.currentTab === 'EXPRESS';
    const tipsToRender = 2;
    const tipsToFetch = 50;

    if (isExpress) {
      const { isRunTipsSkipping = false, currentPage = 1 } = params || {};

      const betsLength = this.bets.length;

      const { disciplinesIds, eventsIds } = this.bets.reduce(
        (acc, bet, i) => {
          const { eventId, disciplineId } = bet;
          let pureEventId = eventId;
          if (eventId.includes('.')) {
            pureEventId = eventId.split('.').pop();
          }

          acc.eventsIds.add(pureEventId);
          acc.disciplinesIds.add(disciplineId);
          if (betsLength === i + 1) {
            return {
              disciplinesIds: [...acc.disciplinesIds],
              eventsIds: [...acc.eventsIds],
            };
          }
          return acc;
        },
        { disciplinesIds: new Set(), eventsIds: new Set() },
      );

      try {
        if (isRunTipsSkipping) {
          if (this.expressTipsFromFetch.length) {
            this.expressTipsShowed = [...this.expressTipsShowed, ...this.expressTips];
            await this.updateExpressTips(this.expressTipsFromFetch.splice(0, tipsToRender));
            this.hasExpressTipsInStorage = !!this.expressTipsFromFetch.length;

            if (!(this.hasExpressTipsOnServer || this.expressTipsFromFetch.length)) {
              this.expressTipsShowed = [];
              await this.getExpressTips();
            }
            if (this.expressTipsFromFetch.length || !this.hasExpressTipsOnServer) return;
          }
          this.expressTipsCurrentPage++;
        } else {
          this.expressTipsCurrentPage = currentPage;
        }

        const {
          data: {
            data: expressTipsData,
            meta: { totalRows, pageNumber },
          },
        } = await getExpressRecommendationTips({
          disciplinesIds,
          eventsIds,
          pageNumber: this.expressTipsCurrentPage,
          pageSize: tipsToFetch,
        });

        const numOfTipsInDB = Number(totalRows);
        const loadedTips =
          numOfTipsInDB > tipsToFetch ? tipsToFetch * this.expressTipsCurrentPage : numOfTipsInDB;

        const expressTipsFiltered = expressTipsData.filter(
          ({ outcomeId }) => !this.expressTipsShowed.some(({ id }) => id === outcomeId),
        );

        this.hasExpressTipsOnServer = numOfTipsInDB > loadedTips;
        this.hasExpressTipsInStorage =
          this.hasExpressTipsOnServer || expressTipsFiltered.length > tipsToRender;

        if (!expressTipsFiltered.length) {
          if (!this.hasExpressTipsOnServer) {
            this.resetExpressTips();
          } else {
            await this.getExpressTips({ currentPage: Number(pageNumber) + 1 });
          }
          return;
        }

        await this.updateExpressTips(expressTipsFiltered.splice(0, tipsToRender));
        this.expressTipsFromFetch = expressTipsFiltered;
      } catch (error) {
        logger.error('getExpressTips error', {
          error,
        });

        this.resetExpressTips();
      }
    }
  };

  getBonuses = async () => {
    const {
      auth: { token, brand },
      profile: {
        currency: { name },
      },
      ui: { disciplineIds },
    } = this.root;

    if (!token) {
      return;
    }

    const apiKey = brand === 'beterdemo' ? 'turbodemo' : brand;
    try {
      const {
        data: { bonuses },
      } = await getPlayerBonuses({
        apiKey,
        token,
      });
      const profileCurrency = name.toUpperCase();

      const allowedBonusesByDisciplines = bonuses.reduce((acc, bonus) => {
        const filteredDisciplines = bonus.disciplines.filter((did) => disciplineIds.includes(did));
        if (filteredDisciplines.length) {
          acc.push({ ...bonus, disciplines: filteredDisciplines });
        }
        return acc;
      }, []);

      this.bonuses = allowedBonusesByDisciplines.filter(
        ({ status, availableBetCount, currency }) =>
          (status === 'active' || status === 'activated') &&
          availableBetCount &&
          profileCurrency === currency,
      );
      this.allBonuses = allowedBonusesByDisciplines.filter(
        ({ status, availableBetCount }) =>
          (status === 'active' || status === 'activated') && availableBetCount,
      );
    } catch (error) {
      logger.error('getBonusesError error', {
        error,
      });
    }
  };

  getBonusesByTimeGap = () => {
    const timeGap = 60;
    if (this.bonusesTimestamp) {
      const currentTimestamp = Date.now();
      const isGetBonuses = (currentTimestamp - this.bonusesTimestamp) / 1000 > timeGap;
      if (!isGetBonuses) {
        return;
      }
    }
    this.bonusesTimestamp = Date.now();
    this.getBonuses();
  };

  placeBonusBet = async (bonusBet) => {
    if (Array.isArray(bonusBet) && bonusBet.length) {
      const [
        {
          bonus: { betAmount, id },
        },
      ] = bonusBet;
      this.bonusBetInProgressId = id;
      this.expressAmount = betAmount;
      this.makeExpressBet({
        bets: bonusBet.map(({ bet }) => bet),
        bonusId: id,
      });
      return;
    }
    const { bet, bonus } = bonusBet;
    this.bonusBetInProgressId = bet.id;
    this.makeBet(false, [{ ...bet, bonusId: bonus.id, stake: bonus.betAmount }]);
  };

  /**
   * Get min error from betsStatuses
   * @param {Array} betStatuses coupon bet statuses
   * @returns {string} min amount to place bet
   */
  expressMinError = (betStatuses) => {
    let parlayMinError = '';
    betStatuses.forEach((item) => {
      if (item && 'errors' in item && item.errors && item.errors.length) {
        item.errors.forEach((i) => {
          if (i.key === 'min') {
            parlayMinError = i.value[0];
          }
        });
      }
    });
    return parlayMinError;
  };

  clearBetStatusesErrors = () => {
    this.betStatuses = this.betStatuses.map((item) => {
      item.errorCode = null;
      item.errors = [];
      return item;
    });
  };

  getExpressAmount = (amount) => {
    const stakeValue = String(amount)
      .replace(/[^\d(.|,)-]/g, '')
      .replace(',', '.');
    const n = parseFloat(stakeValue);
    return Number.isFinite(n) ? n : null;
  };

  /**
   * @param {number} expressAmount
   * @param {number} expressOdd
   *
   * @returns {number}
   */
  getExpressPossibleWin = (expressAmount, expressOdd) => {
    const { roundCurrency } = this.root.profile;

    return roundCurrency(expressAmount * expressOdd) || 0;
  };

  setExpressAmount = (amount) => {
    if (!this.inProgress) {
      this.expressAmount = this.getExpressAmount(amount);
    }
  };

  setExpressAmountPro = (amount) => {
    this.expressAmount = amount;
  };

  /**
   * Set betslib tab
   * @param {String} tab - "SINGLEBET" | "EXPRESS" | "QUICK-BETS"
   * @memberof Betslip
   */
  setCurrentTab = (tab) => {
    if (this.currentTab === tab) return;
    if (!this.root.shareBet.isShareBetEnterCode && tab !== 'QUICK-BETS' && !this.bets.length) {
      if (!tab) {
        this.currentTab = '';
      }
      return;
    }

    if (this.inProgress) return;

    this.clearBetStatusesErrors();

    switch (tab) {
      case 'SINGLEBET':
        if (this.notices.duplicates) this.setNotice('duplicates', false);
        if (this.notices.comboOutrights) this.setNotice('comboOutrights', false);
        this.setIsDisabledPlacebetButton(false);
        this.setIsQuickBetsMode(false);
        break;
      case 'EXPRESS':
        if (this.duplicates.length) {
          this.setNotice('duplicates', true);
          this.setIsDisabledPlacebetButton(true);
        }
        if (this.comboOutrights.length) {
          this.setNotice('comboOutrights', true);
          this.setIsDisabledPlacebetButton(true);
        }
        this.setIsQuickBetsMode(false);
        break;
    }

    this.currentTab = tab;

    if (tab !== 'QUICK-BETS') {
      if (tab === 'EXPRESS' && this.bets.length === 1) {
        this.currentTab = 'SINGLEBET';
      }
    }

    LS.set('esBetslipTab', this.currentTab);
  };

  toggleAcceptChanges = () => {
    if (!this.inProgress) {
      LS.set('esAcceptChanges', !this.acceptChanges);
      this.acceptChanges = !this.acceptChanges;
    }
  };

  setIsShowComboBooster = (bool) => {
    this.isShowComboBooster = bool;
  };

  setNotice = (type, state) => {
    this.notices[type] = state;
  };

  resetData = () => {
    this.pendingBets = [];
    this.betStatuses = [];
    this.setMaxDelay();
  };

  resetStatuses = () => {
    this.pendingBets = [];
    this.betStatuses = [];
  };

  setInProgress = (inProgress) => {
    this.inProgress = inProgress;
    if (!inProgress) {
      this.bonusBetInProgressId = null;
    }
  };

  addBet = (bet, isRestoredFromLS = false) => {
    if (this.inProgress) return;

    const { pidLid, id } = bet;
    this.root.store.updateOutcomeById(id, pidLid, 'isActive', true);
    sendMsg('addBet', {
      id,
    });
    this.bets.push(bet);
    this.updateInitialBet(bet);

    analytics.pushBetInfo({
      action: dataLayerActions.ADD_BET,
      bets: this.bets,
      isRestoredFromLS,
    });
  };

  remove = (id) => {
    // remove BET
    const {
      bets,
      duplicates,
      setNotice,
      comboOutrights,
      inProgress,
      root: { store },
    } = this;
    const bet = bets.find((bet) => bet.id === id);

    if (inProgress || !bet) return;

    const { eventId, pidLid, isBetBuilder = false } = bet;
    store.updateOutcomeById(id, pidLid, 'isActive', false);

    if (eventId) {
      if (duplicates.length) {
        const filteredDuplicates = duplicates.filter((id) => id !== eventId);
        if (!filteredDuplicates.length) {
          setNotice('duplicates', false);
          this.setIsDisabledPlacebetButton(false);
        }
      }

      if (comboOutrights.length) {
        const filteredOutright = comboOutrights.filter((id) => id !== eventId);
        if (!filteredOutright.length) {
          setNotice('comboOutrights', false);
          this.setIsDisabledPlacebetButton(false);
        }
      }
    }

    sendMsg('removeBet', {
      eventId,
      outcomeId: id,
    });

    const filteredBets = bets.filter((bet) => bet.id !== id);
    bets.replace(filteredBets);
    this.removeFromStorage(id);
    this.removeInitialBet(id);
    if (isBetBuilder) {
      this.root.betBuilder.removeBetBuilderItem({ eventId });
    }
    if (!bets.length) {
      this.root.ui.setBetSlipOpen(false);
    }
    analytics.pushBetInfo({ action: dataLayerActions.REMOVE_BET, bets: [bet] });
  };

  updateById = (betId, propName, value) => {
    const index = this.bets.findIndex(({ id }) => id === betId);
    if (index !== -1) {
      const bet = this.bets[index];
      set(bet, propName, value);
      if (propName === 'price') {
        bet.addBetHistoryItem(value);
        this.updateStorage(betId, bet.betHistory);
      }
    }
  };

  updateStorage(betId, history) {
    const bets = JSON.parse(LS.get('esSingleBets'));
    if (bets) {
      // fix map of null error
      bets.map((item) => {
        if (betId === item.id) {
          item.betHistory = history;
        }
      });
      LS.set('esSingleBets', JSON.stringify(bets));
    }
  }

  setIsUpdateExpressPreset = (bool = false) => {
    this.isUpdateExpressPreset = bool;
  };

  updateBetsPropsAfterLangChange(oldBets, newBets) {
    oldBets.forEach((bet) => {
      const { id: betId, pidLid: betPidLid } = bet;
      const market = newBets.find(({ lid }) => lid === betPidLid);

      if (market) {
        const { name: outcomeName } = this.root.store.getOutcomeByLID(betId, betPidLid) || {};
        const { name: eventName } = this.root.store.events.get(market.pid) || {};

        bet.updateProp('name', outcomeName);
        bet.updateProp('eventName', eventName);
        bet.updateProp('marketName', market.name);
      }
    });
  }

  //Update bets market names if language is changed
  async updateBets() {
    const removeMissingSocketBets = (markets) => {
      const marketsIds = markets.map(({ lid }) => lid);
      const betsIds = this.bets.map(({ pidLid }) => pidLid);
      const betsToRemove = difference(betsIds, marketsIds);

      betsToRemove.forEach((pidLidToRemove) => {
        const betToRemove = this.bets.find(({ pidLid }) => pidLid === pidLidToRemove);
        if (betToRemove) {
          this.remove(betToRemove.id);
        }
      });
    };

    // update express tips
    if (this.expressTips.length) {
      const wsMarkets = await this.subscribeOnMarketsInSW({
        marketsPids: this.expressTips.reduce(
          (acc, { id, pidLid }) => ({ ...acc, ...{ [id]: pidLid } }),
          {},
        ),
      });
      const { resolved: markets } = wsMarkets;
      this.updateBetsPropsAfterLangChange(this.expressTips, markets);
    }
    // update bets
    if (this.bets.length) {
      const wsMarkets = await this.subscribeOnMarketsInSW({
        marketsPids: this.bets.reduce(
          (acc, { id, pidLid }) => ({ ...acc, ...{ [id]: pidLid } }),
          {},
        ),
      });
      const { resolved: markets } = wsMarkets;

      removeMissingSocketBets(markets);
      this.updateBetsPropsAfterLangChange(this.bets, markets);
    }
    // update liveBet
    if (Object.keys(this.liveBet).length) {
      const wsMarkets = await this.subscribeOnMarketsInSW({
        marketsPids: {
          [this.liveBet.id]: this.liveBet.pidLid,
        },
      });
      const { resolved: markets } = wsMarkets;

      const market = markets.find(({ lid }) => lid === this.liveBet.pidLid);
      if (market) {
        this.liveBet.updateProp('marketName', market.name);
      }
    }

    this.setIsUpdateExpressPreset(true);
    this.getExpressPresets(this.root.ui.disciplineIds, () => {});
  }

  prepareWidgetBets = (data) => {
    const lineTypes = ['1', '2'];
    return (
      data.reduce((acc, { id = '', pid = '' }) => {
        const [eventId = '', marketId = ''] = pid.split('.').slice(-2);
        lineTypes.forEach((line) => {
          acc.push({
            eventId: `${line}.${eventId}`,
            id,
            pidLid: `${line}.${eventId}.${marketId}`,
          });
        });
        return acc;
      }, []) || []
    );
  };

  /**
   * Gets data from localstorage, and if there is - subscribes to markets and listens to socket
   * @return {void}
   */
  restoreData = async (shareBetData = []) => {
    const acceptChanges = LS.get('esAcceptChanges');
    if (acceptChanges !== undefined && acceptChanges !== null) {
      this.acceptChanges = acceptChanges === 'true';
    }

    if (this.bets.length) {
      return;
    }

    const widgetsBets = [
      ...this.prepareWidgetBets(JSON.parse(LS.get('liveWidgetBets') || '[]')),
      ...this.prepareWidgetBets(JSON.parse(LS.get('topWidgetBets') || '[]')),
    ];

    if (widgetsBets.length) {
      this.root.shareBet.setIsShareBetEnterCode(true);
    }

    const items = shareBetData.length
      ? prepareShareBetData(shareBetData)
      : [
          ...(JSON.parse(LS.get('esSingleBets') || '[]').filter((item) => item) || []),
          ...widgetsBets,
        ];

    LS.remove('liveWidgetBets');
    LS.remove('topWidgetBets');

    if (!items.length) {
      return;
    }

    const { isShareBetEnterCode } = this.root.shareBet;
    const { eventsIds, marketsIds } = extractEventAndMarketIds(
      items,
      isShareBetEnterCode,
      this.root.betBuilder?.isBetBuilderActive,
    );

    if (!marketsIds.length) {
      return;
    }

    // await getFromWebSocket({
    //   entityName: 'event',
    //   ids: eventsIds,
    // });

    const { resolved: resolvedMarkets } = await getFromWebSocket({
      entityName: 'market',
      ids: marketsIds,
    });

    await this.restoreBets({ bets: items, markets: resolvedMarkets });

    this.finishRestore();

    const savedTab = LS.get('esBetslipTab') || null;
    const isExpressTab = new Set(items.map((item) => item.id)).size > 1;
    if (isExpressTab && !shareBetData.length && !savedTab) {
      this.setCurrentTab('EXPRESS');
    }
  };

  /*
   * Restore bets from localstorage or sharebet data
   * @param {Array} markets - Array of markets from websocket
   * @param {Array} bets - Array of bets from localstorage or sharebet data
   * @return {void}
   */
  restoreBets = async ({ markets, bets }) => {
    const { isShareBetEnterCode } = this.root.shareBet;

    const outcomesWithMarkets = markets.reduce((acc, market) => {
      (market.outcomes || []).forEach((outcome) => {
        const { outcomes: _, ...formattedMarket } = market;
        outcome.market = { ...formattedMarket };
        acc.push(outcome);
      });
      return acc;
    }, []);

    bets.forEach(async (bet) => {
      const isBetExist = this.bets.find(({ id }) => id === bet.id);

      if (isBetExist) {
        return;
      }

      // Restore bet by sharebet code
      if (isShareBetEnterCode) {
        if (bet.isBetBuilder) {
          bet.betBuilderOutcomes.forEach(async (outcome) => {
            const targetOutcome = outcomesWithMarkets.find(
              ({ id: outcomeId }) => outcomeId === outcome.outcomeId,
            );

            if (!targetOutcome) {
              return;
            }

            const { market = {}, id: outcomeId, price } = targetOutcome;
            const { lid: marketLid = '', name: marketName = '' } = market;

            await this.root.shareBet.restoreShareBet({
              isBetBuilder: true,
              item: bet,
              marketLid,
              marketName,
              outcome: targetOutcome,
            });
          });

          return;
        }

        const targetOutcome = outcomesWithMarkets.find(
          ({ id: outcomeId, pid }) => outcomeId === bet.outcomeId && bet.marketId === pid,
        );

        if (!targetOutcome) {
          return;
        }

        const { market = {}, id: outcomeId, price } = targetOutcome;
        const { lid: marketLid = '', name: marketName = '' } = market;

        await this.root.shareBet.restoreShareBet({
          isBetBuilder: false,
          item: bet,
          marketLid,
          marketName,
          outcome: targetOutcome,
        });

        return;
      }

      // Restore bet by localstorage
      if (bet.isBetBuilder) {
        const isBetBuilderWithOutcomes =
          Boolean(bet.betBuilderOutcomes.length) && this.root.betBuilder?.isBetBuilderActive;

        isBetBuilderWithOutcomes && (await this.root.betBuilder.restoreBetBuilder(bet));
        return;
      }

      const targetOutcome = outcomesWithMarkets.find(({ id: outcomeId }) => outcomeId === bet.id);

      if (!targetOutcome || targetOutcome.isFrozen) {
        return;
      }

      const { market = {}, id: outcomeId, price } = targetOutcome;
      const { id: marketLid = '', name: marketName = '' } = market;

      this.root.store.updateOutcomeById(outcomeId, marketLid, 'isActive', true);
      /**
       * Create new Bet instance and relying on info from outcome
       * because bet from localstorage can be outdated and can be in live already
       */

      const newBet = new Bet(
        {
          ...bet,
          competitorId: targetOutcome.competitorId,
          eventId: targetOutcome.eventId,
          eventName: bet.eventName,
          marketName,
          name: targetOutcome.name,
          pid: targetOutcome.pid,
          pidLid: targetOutcome.pid,
          price: price,
        },
        this.root.store,
        this,
      );
      this.addBet(newBet, true);
    });
  };

  /**
   * Final step of bets restoration process
   * Check bonuses and process pending bets if exist
   * Check is sharebet mode and show betslip if needed
   * @return {void}
   */
  finishRestore = () => {
    this.getBonusesByTimeGap();

    const { setBetSlipOpen, isMobile, setAlert } = this.root.ui;

    if (this.root.shareBet.isShareBetEnterCode && this.bets.length) {
      if (isMobile) {
        setBetSlipOpen(true);
      }
      const { shareBetDataLength } = this.root.shareBet;
      if (shareBetDataLength && this.bets.length < shareBetDataLength) {
        setAlert({
          text: 'SHAREBET.ERROR.SOME_MARKETS',
          type: 'warning',
        });
      }
    }

    runInAction(() => {
      this.root.shareBet.setIsShareBetEnterCode(false);
      this.root.shareBet.setShareBetSlipTab(null);
    });

    if (!this.ticketsToRestore.length) {
      return;
    }

    this.setInProgress(true);
    this.processPendingBets({
      isRestore: true,
      pendingBets: this.ticketsToRestore,
    });
  };

  /**
   * Filter localstorage by current bets in betslip
   * @return {void}
   */
  filterStorage = () => {
    const betIds = this.bets.map(({ id }) => id);
    const esSingleBets = LS.get('esSingleBets');
    if (esSingleBets) {
      const items = JSON.parse(esSingleBets)
        .filter((item) => item)
        .filter(({ id }) => betIds.includes(id));
      LS.set('esSingleBets', JSON.stringify(items));
    }
  };

  clearStorage() {
    LS.remove('esSingleBets');
  }

  addToStorage = (bet) => {
    const bets = LS.get('esSingleBets');
    const storedBets = JSON.parse(bets) || [];
    const items = storedBets.filter((item) => item && bet.id !== item.id);
    const { store, bets: bts, ...rest } = bet;
    items.push(rest);
    LS.set('esSingleBets', JSON.stringify(items));
  };

  addTicketIdToStorageBet(betId, ticketId) {
    const esSingleBets = LS.get('esSingleBets');
    if (esSingleBets) {
      const items = JSON.parse(esSingleBets).filter((item) => item);
      const bet = items.find(({ id }) => id === betId);
      if (bet) {
        bet.ticketId = ticketId;
        this.addToStorage(bet);
      }
    }
  }

  removeFromStorage(id) {
    const bets = LS.get('esSingleBets');
    const storedBets = JSON.parse(bets) || [];
    const items = storedBets.filter((item) => item && item.id !== id);
    LS.set('esSingleBets', JSON.stringify(items));
  }

  isEmptyStorage = () => {
    const bets = LS.get('esSingleBets');
    const storedBets = JSON.parse(bets) || [];
    return !storedBets.length;
  };

  setBlocking = (betId) => {
    this.setComboBlockingError(betId);
    if (!this.isBlockingCombo) {
      if (this.currentTab === 'EXPRESS') {
        this.root.ui.setAlert({
          isSingle: true,
          progress: true,
          text: 'COMMON.EXPRESS.RULE.MAIN',
          type: 'warning',
        });
      }
      this.setCurrentTab('SINGLEBET');
      this.isBlockingCombo = true;
    }
  };

  setComboBlocking = (key) => {
    let isBlockingExpress = false;
    this.bets.forEach((elem) => {
      if (elem.eventId.split('.').pop() === key) {
        isBlockingExpress = true;
        this.setComboBlockingError(elem.id);
      }
    });

    return isBlockingExpress;
  };

  createBlocksIdsMap = () => {
    if (this.isEmptyStorage()) {
      this.setBlocksIdsMap({});
      return;
    }
    const root = this.root.store;
    const idsMap = this.getBlocksIdsMap();
    const getBlockingIds = this.bets?.reduce(
      (acc, bet) => {
        const { eventId = '', isBetBuilder = false } = bet;
        if (isBetBuilder) {
          return acc;
        }
        const idEvent = eventId.split('.').pop();
        const { pid, expressRules: eventExpressRules = [] } = root.events.get(eventId) || {};
        const { id: idTournament, expressRules: tournamentExpressRules = [] } =
          root.tournaments.get(pid) || {};

        if (idsMap[`${idEvent}`]) {
          acc.blocksIdsMap[`${idEvent}`] = idsMap[`${idEvent}`];
          acc.tournamentIds.push(idsMap[`${idEvent}`].idTournament);
          acc.tournamentBlockingIds.push(
            ...idsMap[`${idEvent}`].tournamentExpressRules.filter(
              (_id) => !acc.tournamentBlockingIds.includes(_id),
            ),
          );
          acc.eventBlockingIds.push(
            ...idsMap[`${idEvent}`].eventExpressRules.filter(
              (_id) => !acc.eventBlockingIds.includes(_id),
            ),
          );
        } else {
          acc.blocksIdsMap[`${idEvent}`] = {
            eventExpressRules,
            idTournament,
            tournamentExpressRules,
          };

          acc.tournamentIds.push(idTournament);
          acc.tournamentBlockingIds.push(
            ...tournamentExpressRules.filter((_id) => !acc.tournamentBlockingIds.includes(_id)),
          );
          acc.eventBlockingIds.push(
            ...eventExpressRules.filter((_id) => !acc.eventBlockingIds.includes(_id)),
          );
        }

        return acc;
      },
      {
        blocksIdsMap: {},
        eventBlockingIds: [],
        tournamentBlockingIds: [],
        tournamentIds: [],
      },
    );

    this.setBlocksIdsMap(getBlockingIds?.blocksIdsMap);
    return getBlockingIds;
  };

  comboBlockingHandler = () => {
    const blockingIdsData = this.createBlocksIdsMap();
    if (!blockingIdsData) return;

    const { blocksIdsMap, eventBlockingIds, tournamentBlockingIds, tournamentIds } =
      blockingIdsData;

    const isTournamentBlocking = tournamentIds.some((id) => tournamentBlockingIds.includes(id));
    const isEventBlocking = Object.keys(blocksIdsMap).some((id) => eventBlockingIds.includes(id));

    if (!(isTournamentBlocking || isEventBlocking) || this.bets.length <= 1) {
      this.resetComboBlockingError();
      this.isBlockingCombo = false;
      this.setIsDisabledPlacebetButton(false);
      return;
    }

    if (this.currentTab === 'EXPRESS' && (isTournamentBlocking || isEventBlocking)) {
      this.setIsDisabledPlacebetButton(true);
    }

    this.bets.forEach((bet) => {
      const eventId = bet.eventId.split('.').pop();
      const { idTournament, eventExpressRules, tournamentExpressRules } =
        blocksIdsMap[`${eventId}`] || {};
      if (!(eventBlockingIds.includes(eventId) || tournamentBlockingIds.includes(idTournament))) {
        this.resetComboBlockingErrorById(bet.id);
      } else if (eventExpressRules.length) {
        if (eventExpressRules[0] === eventId) {
          this.setBlocking(bet.id);
        } else {
          let isBlockingExpress = false;
          Object.keys(blocksIdsMap).forEach((key) => {
            if (eventExpressRules.includes(key)) {
              isBlockingExpress = this.setComboBlocking(key);
            }
          });
          if (isBlockingExpress) {
            this.setBlocking(bet.id);
          }
        }
      } else if (tournamentExpressRules.length) {
        if (tournamentExpressRules[0] === idTournament) {
          this.setBlocking(bet.id);
        } else {
          let isBlockingExpress = false;
          Object.entries(blocksIdsMap).forEach(([key, { idTournament: id }]) => {
            if (tournamentExpressRules.includes(id)) {
              isBlockingExpress = this.setComboBlocking(key);
            }
          });
          if (isBlockingExpress) {
            this.setBlocking(bet.id);
          }
        }
      }
    });
  };

  getBlocksIdsMap = () => {
    const blocksIdsMap = LS.get('blocksIdsMap');
    return JSON.parse(blocksIdsMap) || {};
  };

  setBlocksIdsMap = (blocksIdsMap) => {
    LS.set('blocksIdsMap', JSON.stringify(blocksIdsMap));
  };

  setComboBlockingError = (betId) => {
    this.bets.find(({ id }) => id === betId).comboBlocking = true;
  };

  resetComboBlockingErrorById = (betId) => {
    this.bets.find(({ id }) => id === betId).comboBlocking = false;
  };

  resetComboBlockingError = () => {
    this.bets.forEach((bet) => {
      bet.comboBlocking = false;
    });
  };

  /**
   * Removes successful bets from the popular bets widget based on their statuses.
   *
   * @param betIdToRemove - Id of bet to remove from popular bets widget.
   */
  removeSuccessfulBetsFromPopularBetsWidget(betIdToRemove) {
    const index = this.popularBets.findIndex(({ outcomeId }) => outcomeId === betIdToRemove);

    if (index !== -1) {
      const bet = this.popularBets[index];
      this.handleSettledBetsByLS({
        bets: [{ presetId: bet.outcomeId, startTime: bet.event.startTime }],
        lsKey: 'settledPopularBets',
      });
      this.popularBets.splice(index, 1);
    }
  }

  removeSuccessfulBet = (id) => {
    this.root.betslipErrors.isRemovingAfterPlaceBet = true;
    const { bets } = this;
    const filteredBets = bets.filter((bet) => bet.id !== id);
    bets.replace(filteredBets);
    this.removeSuccessfulBetsFromPopularBetsWidget(id);
    this.removeFromStorage(id);
    this.removeInitialBet(id);
  };

  checkValid = () => {
    const { bets, comboOutrights, currentTab, duplicates } = this;
    if (currentTab !== 'EXPRESS') return;
    bets.forEach(({ eventId }) => {
      if (duplicates.includes(eventId)) {
        this.setCurrentTab('SINGLEBET');
        LS.set('esBetslipTab', 'SINGLEBET');
      }
      if (comboOutrights.includes(eventId)) {
        this.setCurrentTab('SINGLEBET');
        LS.set('esBetslipTab', 'SINGLEBET');
      }
    });
  };

  /**
   * @param {object} params
   * @param {string} params.outcomeId
   * @param {string} params.marketId
   * @param {Bet=} params.customBet
   * @param {(string|string[])=} params.trackMarks
   */
  add = ({ outcomeId = '', marketId = '', customBet, trackMarks = ['default'] }) => {
    const {
      bets,
      inProgress,
      root: { ui },
    } = this;
    if (inProgress) {
      return;
    }

    const betExists = bets.some(({ id }) => id === outcomeId);
    if (betExists) {
      return;
    }

    const bet = customBet || this.initNewBet({ id: outcomeId, marketId, trackMarks });

    if (!bet) {
      return;
    }

    ui.setActiveTab('betslip');
    this.getBonusesByTimeGap();
    this.addBet(bet);
    // using normalized bet to ensure that after reload we will subscribe to normal events in noraml line
    this.addToStorage(normalizeBetForLocalStorage(bet));
    this.checkValid();
  };

  addLiveBet = (outcomeId, marketId) => {
    const { liveBet, inProgress } = this;
    if (!inProgress) {
      if (!outcomeId) return;
      const betExists = liveBet.id === outcomeId;
      if (betExists) {
        this.setLiveBet({});
        return;
      }

      const bet = this.initNewBet({
        id: outcomeId,
        marketId,
        trackMarks: betTrackMark.liveBet,
      });
      if (!bet) return;
      this.setLiveBet(bet);
      this.root.store.updateOutcomeById(outcomeId, marketId, 'isActive', true);
      sendMsg('addBet', {
        id: outcomeId,
      });
      analytics.pushBetInfo({
        action: dataLayerActions.ADD_BET,
        bets: [bet],
        currentBetType: 'live_bet',
      });
    }
  };

  /**
   * Set stake of single bet
   * @param betId
   * @param stake
   */
  setStake = (betId, stake = '') => {
    if (!this.inProgress) {
      if (!betId) return;

      const stakeValue = String(stake)
        .replace(/[^\d(.|,)-]/g, '')
        .replace(',', '.');
      this.updateById(betId, 'stake', parseFloat(stakeValue || 0));
      this.bets.forEach((item, index) => {
        if (item.id === betId) {
          this.addToStorage(this.bets[index]);
        }
      });
    }
  };

  valid(bets = [], isExpressPresets = false) {
    const {
      auth: { isLogged },
    } = this.root;

    let valid = true;

    if (!isLogged || this.inProgress) {
      return false;
    }

    this.betStatuses = [];

    const { duplicates, comboOutrights, currentTab: currentTabFromBetslip } = this;
    const currentTab = isExpressPresets ? 'EXPRESS' : currentTabFromBetslip;

    const deleted = 'RX.BETSLIP.ERRORS.EVENT_FINISHED';

    const errorBetStatus = {
      // 'Your Bet Slip cannot include suspended or closed outcome'
      errorCode: 9,
      ids: [], // 'BETSLIP.ERRORS.CLOSED_MARKETS'
    };

    bets.forEach((item) => {
      const { id, pidLid, isBetBuilder = false } = item;
      const outcome = this.root.store.getOutcomeByLID(id, pidLid);
      const errors = [];

      if (currentTab === 'SINGLEBET') {
        valid = true;
      }

      if (currentTab === 'EXPRESS' && (duplicates.length || comboOutrights.length)) {
        if (duplicates.length) {
          this.setNotice('duplicates', true);
          this.setIsDisabledPlacebetButton(true);
        }
        if (comboOutrights.length) {
          this.setNotice('comboOutrights', true);
          this.setIsDisabledPlacebetButton(true);
        }
        valid = false;
      }
      if (!outcome || (outcome && 'isFrozen' in outcome && outcome.isFrozen)) {
        errors.push({
          key: 'deleted',
          value: [deleted],
        });
        valid = false;
      }
      if (isBetBuilder) {
        valid = true;
      }
      if (!valid) {
        errorBetStatus.ids.push(id);
        this.betStatuses.push({
          errors,
          id,
        });
      }
    });

    if (errorBetStatus.ids.length) {
      this.root.betslipErrors.setErrorBetStatuses(errorBetStatus);
    }

    if (currentTab === 'EXPRESS' && !valid) {
      this.root.betslipErrors.handleBetSlipNotifications();
    }
    return valid;
  }

  /**
   * Handles settled bets by updating and managing the data in local storage.
   *
   * @param {Object} params - Parameters object.
   * @param {string} params.lsKey - The local storage key to retrieve and update settled bets.
   * @param {Array|undefined} params.bets - Array of bets to process. If not provided,
   * the function will only return the actual settled bets.
   * @returns The updated list of actual settled bets.
   */
  handleSettledBetsByLS(params) {
    const { lsKey, bets } = params || {};
    const settledBetsFromStore = this.settledBets[lsKey];
    let settledBetsFromLs = settledBetsFromStore;

    if (!settledBetsFromLs) {
      try {
        let betsFromLs = JSON.parse(LS.get(lsKey));

        if (Array.isArray(betsFromLs)) {
          betsFromLs = Object.fromEntries(betsFromLs.map(({ id, expires }) => [id, expires]));
        }
        settledBetsFromLs = betsFromLs || {};
      } catch (error) {
        console.warn(`Error parsing ${lsKey} from local storage:`, error);
        settledBetsFromLs = {};
      }
    }

    const actualSettledBets =
      settledBetsFromStore ||
      Object.entries(settledBetsFromLs).reduce((acc, [id, expires]) => {
        const isExpired = Number(getTimeFromStart(expires / 1000)) >= 0;

        if (!isExpired) {
          acc[id] = expires;
        }

        return acc;
      }, {});

    if (Array.isArray(bets) && bets.length) {
      const { presetId } = bets[0];

      if (!actualSettledBets[presetId]) {
        const startTimeMax = bets.reduce(
          (acc, { startTime }) => (startTime > acc ? startTime : acc),
          0,
        );
        actualSettledBets[presetId] = getDateByInterval('tomorrow', startTimeMax).getTime();
        LS.set(lsKey, JSON.stringify(actualSettledBets));
      }
    }

    this.settledBets[lsKey] = actualSettledBets;

    return actualSettledBets;
  }

  handleBetSlipFailure = () => {
    this.pendingBets = [];
    this.betStatuses = [];
    this.setMaxDelay();
    this.statusRespondsLength = 0;
    this.failedStatusesLength = 0;
    this.successStatusesLength = 0;
    this.isPendingBets = false;
    this.setIsPendingBetsFinished(false);

    this.root.betslipErrors.setErrorBetStatuses({
      // 'Something went wrong. Please try again later or contact support'
      errorCode: 'defaultError',
      ids: [], // 'BETSLIP.ERRORS.DEFAULT_MESSAGE'
    });
  };

  debouncedMakeExpressBet = debounceAsync(
    (...args) => this.makeExpressBet(...args),
    PLACE_BET_DEBOUNCE_TIME,
  );

  makeExpressBet = async ({
    bets = this.bets,
    isExpressPresets = false,
    expressAmount = this.expressAmount,
    bonusId,
    isExpressPresetsByTournament = false,
    isMixedTurboCombo = false,
  }) => {
    const currentAmount =
      typeof expressAmount === 'number'
        ? expressAmount
        : typeof expressAmount === 'string'
          ? parseFloat(expressAmount)
          : null;

    if (!this.isValidBetAmount({ amount: currentAmount })) {
      return {
        errorCode: 'invalidAmount',
        status: 'error',
      };
    }

    const betType = isExpressPresets || isMixedTurboCombo ? 'expressPresets' : 'express';
    const currentBets = [...bets];

    const {
      auth: { token, subPartnerId },
      ui: { setAlert, setBetSlipOpen },
    } = this.root;
    sendMsg('clickPlaceBet', {
      betType: betType,
    });
    if (!token) {
      setBetSlipOpen(false);
      setAlert({
        isSingle: true,
        title: 'ES.LOGIN.PLACE_BET_SIGN_IN_NOTE',
        type: 'warning',
      });
      sendMsg('placeBetFailure', {
        status: betStatus.iframeMessage.loginErr.text,
        statusId: betStatus.iframeMessage.loginErr.statusId,
      });
      return;
    }

    if (bets.length === 1) {
      this.makeBet(false);
    } else {
      if (isExpressPresets) {
        this.isExpressPresetsInProgress = true;
      }

      this.pendingBets = [];
      this.betStatuses = [];
      const {
        profile: {
          currency: { name },
          user: { visitorId },
        },
      } = this.root;

      if (!(bonusId || this.valid(bets, isExpressPresets || isMixedTurboCombo))) {
        return;
      }

      this.setInProgress(true);

      const preparedBets = bets
        .filter(Boolean)
        .reduce((acc, bet) => [...acc, this.prepareBet(bet, true)], []);

      const getUpdatedExpressPresets = (presets = {}, settledPresetId = '') => {
        return Object.entries(presets).reduce((acc, [did, presets]) => {
          const presetsFiltered = presets.filter((preset) => {
            const { presetId } = preset[0];
            return presetId !== settledPresetId;
          });
          if (!presetsFiltered.length) return acc;

          return { ...acc, [did]: presetsFiltered };
        }, {});
      };

      const acceptChangesExpressPresets = isExpressPresets || isMixedTurboCombo;

      try {
        const { data } = await placeExpress({
          accept_changes: acceptChangesExpressPresets || this.acceptChangesForBets,
          amount: currentAmount,
          bets: preparedBets,
          bonusId,
          currency: name,
          sub_partner_id: subPartnerId,
          visitorId,
        });

        if (data) {
          if (isExpressPresets || isMixedTurboCombo) {
            analytics.pushBetInfo({
              action: dataLayerActions.ADD_BET,
              bets: currentBets,
              currentBetType: isMixedTurboCombo ? 'turbo_combo_bet_slip' : 'turbo_combo',
            });
          }

          const successStatuses = await this.processTicketStatuses({
            betsFromBetslip: currentBets,
            betslipTabName: isExpressPresets || isMixedTurboCombo ? '' : 'EXPRESS',
            isExpressPresets,
            isInitial: true,
            isMixedTurboCombo,
            statuses: data.data,
          });

          this.getDefaultStake([{ stake: currentAmount }]);
          sendMsg('placeBet', {
            amount: currentAmount,
            betType,
            bets: preparedBets,
          });
          if (successStatuses && isExpressPresets) {
            this.handleSettledBetsByLS({ bets, lsKey: 'settledExpressPresets' });

            const { presetId: settledPresetId } = bets[0];

            if (isExpressPresetsByTournament) {
              this.expressPresetsByTournament = getUpdatedExpressPresets(
                this.expressPresetsByTournament,
                settledPresetId,
              );
            } else {
              this.expressPresets = getUpdatedExpressPresets(this.expressPresets, settledPresetId);
            }

            setAlert({
              title: 'ES.COMMON.BET_ACCEPTED',
              type: 'success',
            });
          }

          return Promise.resolve(successStatuses);
        }
      } catch (e) {
        this.handleBetSlipFailure();
        sendMsg('placeBetError', {
          betType,
          error: e.message,
        });
        Promise.resolve(e);
      } finally {
        this.setInProgress(false);
        this.isExpressPresetsInProgress = false;
        this.root.betslipErrors.handleBetSlipNotifications();
      }
    }
  };

  debouncedMakeBet = debounceAsync(
    async (...args) => await this.makeBet(...args),
    PLACE_BET_DEBOUNCE_TIME,
  );

  makeBet = async (bubblePostMessage = true, bets = this.bets) => {
    const {
      auth: { token, subPartnerId },
      ui: { setAlert, setBetSlipOpen },
      profile: {
        currency: { name },
      },
    } = this.root;

    if (bubblePostMessage) {
      sendMsg('clickPlaceBet', {
        betType: 'ordinar',
      });
    }
    if (!token) {
      setBetSlipOpen(false);
      setAlert({
        isSingle: true,
        title: 'ES.LOGIN.PLACE_BET_SIGN_IN_NOTE',
        type: 'warning',
      });
      sendMsg('placeBetFailure', {
        status: betStatus.iframeMessage.loginErr.text,
        statusId: betStatus.iframeMessage.loginErr.statusId,
      });
      return;
    }

    const {
      profile: {
        user: { visitorId },
      },
    } = this.root;

    this.pendingBets = [];
    this.betStatuses = [];

    const placeBet = async () => {
      this.setInProgress(true);
      const currentBets = [...bets];

      const maxBetErrorIds = [];
      const betBuilderCloseErrorIds = [];
      const invalidAmountIds = [];
      const { preparedBets, betBuilderBets } = bets.reduce(
        (acc, bet) => {
          const targetStake =
            typeof bet.stake === 'number'
              ? bet.stake
              : typeof bet.stake === 'string'
                ? parseFloat(bet.stake)
                : null;

          if (!this.isValidBetAmount({ amount: targetStake })) {
            invalidAmountIds.push(bet.id);
            return acc;
          }

          if (bet.isBetBuilder) {
            const { maxBet = null, stake = null, id = '', eventId = '' } = bet;
            const event = prematchFullLine.some(
              (line = '') => !!this.root.store.events?.get(`${line}.${eventId.split('.').pop()}`),
            );
            if (!event || liveFullLine.includes(eventId.split('.')[0])) {
              betBuilderCloseErrorIds.push(id);
              return acc;
            }

            if (maxBet !== null && Number(stake) > Number(maxBet)) {
              maxBetErrorIds.push(id);
              return acc;
            }

            acc.betBuilderBets.push(bet);
            return acc;
          }

          const betToPrepare = this.prepareBet(bet, false);
          if (betToPrepare) {
            const { amount = 0, maxBet = null, outcomeId = '', bonusId = '' } = betToPrepare;
            if (maxBet !== null && Number(amount) > Number(maxBet) && !bonusId) {
              maxBetErrorIds.push(outcomeId);
              return acc;
            }
            acc.preparedBets.push(betToPrepare);
          }
          return acc;
        },
        { betBuilderBets: [], preparedBets: [] },
      );

      if (invalidAmountIds.length) {
        this.root.betslipErrors.setErrorBetStatuses({
          errorCode: 'invalidAmount',
          ids: invalidAmountIds,
        });
      }

      if (betBuilderCloseErrorIds.length) {
        this.root.betslipErrors.setErrorBetStatuses({
          errorCode: 'betBuilderCloseError',
          ids: betBuilderCloseErrorIds,
        });
      }

      if (maxBetErrorIds.length) {
        this.root.betslipErrors.setErrorBetStatuses({
          errorCode: 'maxBetError',
          ids: maxBetErrorIds,
        });
      }

      if (!(preparedBets.length || betBuilderBets.length)) {
        this.setInProgress(false);
        this.root.betslipErrors.handleBetSlipNotifications();
        return;
      }

      const responses = [];
      try {
        if (betBuilderBets.length) {
          if (!this.root?.betBuilder?.isBetBuilderActive) {
            const betBuilderIds = betBuilderBets.map((betBuilder = {}) => ({
              error: false,
              outcomeId: betBuilder.id ?? '',
              trackMarks: betBuilder.trackMarks ?? [betTrackMark.betBuilder],
            }));

            const fakeResponse = {
              bets: betBuilderIds,
              errorCode: 503,
              id: '',
              status: 1,
            };
            responses.push(fakeResponse);
            logger.error(
              'placeBetBuilder errorCode: 503. Bet Builder is not available for this product',
            );
          } else {
            const promises = betBuilderBets.map((bet = {}) => {
              const bets = (bet.betBuilderOutcomes ?? []).map(({ outcomeId = '' }) => ({
                coefficient: bet.coefficient ?? null,
                outcomeId,
                trackMarks: bet.trackMarks ?? [betTrackMark.betBuilder],
              }));

              return placeBetBuilder({
                accept_changes: this.acceptChangesForBets,
                amount: Number(bet.stake) ?? null,
                bets,
                currency: name,
                sub_partner_id: subPartnerId,
                visitorId,
              });
            });

            const results = await Promise.allSettled(promises);

            results.forEach((result, index) => {
              if (result.status === 'fulfilled') {
                const {
                  data: { data = [] },
                } = result.value;
                const [status] = data;
                if (status) {
                  status.bets[0].outcomeId = betBuilderBets[index]?.id ?? '';
                  responses.push(status);
                }
              } else {
                const {
                  response: { data = {} },
                } = result.reason;

                const fakeResponse = {
                  bets: [
                    {
                      error: false,
                      outcomeId: betBuilderBets[index]?.id ?? '',
                    },
                  ],
                  errorCode: data.error?.code ?? 50,
                  id: '',
                  status: 1,
                };
                responses.push(fakeResponse);
                logger.error('placeBetBuilder error for individual bet', {
                  error: result.reason,
                  errorCode: data.error?.code ?? 50,
                });
              }
            });
          }
        }

        if (preparedBets.length) {
          const {
            data: { data = [] },
          } = await place({
            accept_changes: this.acceptChangesForBets,
            bets: preparedBets,
            sub_partner_id: subPartnerId,
            visitorId,
          });
          data.forEach((status) => {
            responses.push(status);
          });
        }

        if (responses.length) {
          const statuses = await this.processTicketStatuses({
            betsFromBetslip: currentBets,
            betslipTabName: 'SINGLEBET',
            isInitial: true,
            statuses: responses,
          });

          this.getDefaultStake(currentBets ?? [{ stake: null }]);

          sendMsg('placeBet', {
            betType: 'ordinar',
            bets: [
              ...preparedBets,
              ...betBuilderBets.map(
                ({
                  betBuilderOutcomes = [],
                  coefficient = null,
                  stake = null,
                  maxBet = null,
                  eventId,
                }) => ({
                  amount: stake,
                  coefficient,
                  currency: name,
                  eventId,
                  isBetBuilder: true,
                  maxBet,
                  outcomes: betBuilderOutcomes.map(({ outcomeId = '' }) => outcomeId),
                }),
              ),
            ],
          });

          return Promise.resolve(statuses);
        }
      } catch (e) {
        this.handleBetSlipFailure();
        this.setInProgress(false);
        sendMsg('placeBetError', {
          betType: 'ordinar',
          error: e.message,
        });
        Promise.resolve(e);
      } finally {
        this.root.betslipErrors.handleBetSlipNotifications();
      }
    };

    if (this.bonusBetInProgressId || this.valid(bets) || !this.inProgress) {
      await placeBet();
    }
  };

  setIsQuickBetsMode = (bool = false) => {
    const { setAlert, isMobile } = this.root.ui;
    const text = i18next.t(bool ? 'ADDITIONAL.QUICK.BET.ACTIVE' : 'ADDITIONAL.QUICK.BET.INACTIVE');

    if (this.isQuickBetsMode !== bool) {
      setAlert({
        timeout: isMobile ? 5000 : 3000,
        title: text,
        type: 'warning',
      });
    }

    this.isQuickBetsMode = bool;
    if (!this.isQuickBetsMode) {
      this.quickBetsSum = this.defaultStake;
      this.isQuickBetsActive = false;
      this.isQuickBetsInputError = false;
    }
  };

  setIsQuickBetsActive = (bool = false) => {
    this.isQuickBetsActive = bool;
  };

  setQuickBetsSum = (sum = '') => {
    const sumStr = String(sum);

    if (sumStr.includes('.')) {
      const numValue = parseFloat(sumStr);
      this.quickBetsSum = Number.isNaN(numValue) ? 0 : sumStr;
    } else {
      this.quickBetsSum = Number(sum) || 0;
    }
  };
  setQuickBetSuccess = (bool = false) => {
    this.quickBetSuccess = bool;
  };

  setIsQuickBetsInputError = (bool = false) => {
    this.isQuickBetsInputError = bool;
  };

  prepareQuickBet = (bet) => {
    const { id, pidLid, bonusId, trackMarks, eventId } = bet;
    const {
      profile: {
        currency: { name },
      },
      store: { getOutcomeByLID },
    } = this.root;

    const outcomeItem = getOutcomeByLID(id, pidLid);
    if (outcomeItem) {
      const result = {
        amount: parseFloat(`${this.quickBetsSum}`) || 0,
        coefficient: parseFloat(outcomeItem.price),
        currency: name,
        eventId,
        outcomeId: id,
        trackMarks,
      };
      if (bonusId) {
        result.bonusId = bonusId;
      }
      return result;
    } else {
      return null;
    }
  };

  setQuickBetsResolveCounter = (isIncrement) => {
    this.quickBetsResolveCounter = isIncrement
      ? this.quickBetsResolveCounter + 1
      : this.quickBetsResolveCounter - 1;
  };

  setQuickBetsSuccessIds = (id) => {
    this.quickBetsSuccessIds.push(id);
  };

  /**
   * Places a quick bet using the provided parameters and returns the server response.
   * @param {Object} params - Parameters for the quick bet.
   * @param {boolean} [params.bubblePostMessage=true] - Whether to send a clickPlaceBet message before placing the bet.
   * @param {string} params.outcomeId - The identifier of the outcome to bet on.
   * @param {string} params.marketId - The market identifier associated with the outcome.
   * @param {string[]} params.trackMarks - The track mark(s) used for analytics or bonus purposes.
   * @returns {Promise<{response: object, bet?: Bet}>} A promise that resolves with the bet response and the Bet instance.
   */
  makeQuickBet = asyncCooldown(
    async ({
      bubblePostMessage = true,
      outcomeId,
      marketId,
      trackMarks = [betTrackMark.quickBet],
    }) => {
      const bet = this.initNewBet({
        id: outcomeId,
        marketId,
        trackMarks,
      });
      if (!bet) return;

      const {
        auth: { token, subPartnerId },
        ui: { setAlert },
      } = this.root;

      if (bubblePostMessage) {
        sendMsg('clickPlaceBet', {
          betType: 'ordinar',
        });
      }
      if (!token) {
        setAlert({
          isSingle: true,
          title: 'ES.LOGIN.PLACE_BET_SIGN_IN_NOTE',
          type: 'warning',
        });
        sendMsg('placeBetFailure', {
          status: betStatus.iframeMessage.loginErr.text,
          statusId: betStatus.iframeMessage.loginErr.statusId,
        });
        return {
          response: {
            error: 'Not logged',
          },
        };
      }

      const {
        profile: {
          user: { visitorId },
        },
      } = this.root;

      const placeBet = async () => {
        const preparedBet = this.prepareQuickBet(bet);
        const currentAmount =
          Boolean(preparedBet) && 'amount' in preparedBet && typeof preparedBet.amount === 'number'
            ? preparedBet.amount
            : typeof preparedBet.amount === 'string'
              ? parseFloat(`${preparedBet.amount}`) || 0
              : null;

        if (!this.isValidBetAmount({ amount: currentAmount })) {
          return {
            response: {
              errorCode: 'invalidAmount',
              status: 'error',
            },
          };
        }
        const preparedBets = [preparedBet];

        try {
          const { data } = await place({
            accept_changes: this.acceptChangesForBets,
            bets: preparedBets,
            sub_partner_id: subPartnerId,
            visitorId,
          });

          if (data) {
            sendMsg('placeBet', {
              betType: 'ordinar',
              bets: preparedBets,
            });

            this.getDefaultStake([{ stake: this.quickBetsSum ?? null }]);
            if (data?.data.legnth) {
              const entry = data.data[0];

              if (entry.status === 2) {
                this.emitCefChangedAlert(entry.coefficient, entry.placebetCoefficient);
              }
            }

            analytics.pushBetInfo({
              action: dataLayerActions.ADD_BET,
              bets: [bet],
              currentBetType: 'quick_bet',
            });

            return { response: data.data[0] };
          }
        } catch (e) {
          sendMsg('placeBetError', {
            betType: 'ordinar',
            error: e.message,
          });
          console.error(`Can't place quick bet: ${e.message}`);

          return {
            response: {
              placeBetError: e.message,
            },
          };
        }
      };

      if (this.valid([bet])) {
        const { response } = await placeBet();
        return { bet, response };
      } else {
        return { response: this.betStatuses[0] };
      }
    },
    PLACE_BET_DEBOUNCE_TIME,
  );

  makeLiveBet = async (bubblePostMessage = true) => {
    const {
      auth: { token, subPartnerId },
      ui: { setAlert, setBetSlipOpen },
    } = this.root;

    if (bubblePostMessage) {
      sendMsg('clickPlaceBet', {
        betType: 'ordinar',
      });
    }
    if (!token) {
      setBetSlipOpen(false);
      setAlert({
        isSingle: true,
        title: 'ES.LOGIN.PLACE_BET_SIGN_IN_NOTE',
        type: 'warning',
      });
      sendMsg('placeBetFailure', {
        status: betStatus.iframeMessage.loginErr.text,
        statusId: betStatus.iframeMessage.loginErr.statusId,
      });
      return;
    }

    const {
      profile: {
        user: { visitorId },
      },
    } = this.root;

    this.pendingBets = [];
    this.betStatuses = [];

    const targetStake =
      typeof this.liveBet.stake === 'number'
        ? this.liveBet.stake
        : typeof this.liveBet.stake === 'string'
          ? parseFloat(this.liveBet.stake)
          : null;

    if (!this.isValidBetAmount({ amount: targetStake })) {
      this.root.betslipErrors.setErrorBetStatuses({
        errorCode: 'invalidAmount',
        ids: [this.liveBet?.id],
      });

      this.setInProgress(false);
      return;
    }

    if ((this.liveBet.id && this.valid([this.liveBet])) || !this.inProgress) {
      this.setInProgress(true);

      const bets = [this.prepareBet(this.liveBet, false)].filter(Boolean);
      try {
        const { data } = await place({
          accept_changes: this.acceptChangesForBets,
          bets,
          sub_partner_id: subPartnerId,
          visitorId,
        });

        if (data) {
          const statuses = await this.processLiveTicketStatuses(data.data[0], true);

          this.getDefaultStake(bets ?? [{ stake: null }]);

          sendMsg('placeBet', {
            betType: 'ordinar',
            bets,
          });

          return Promise.resolve(statuses);
        }
      } catch (e) {
        sendMsg('placeBetError', {
          betType: 'ordinar',
          error: e.message,
        });
        Promise.resolve(e);
      } finally {
        this.setInProgress(false);
        this.root.betslipErrors.handleBetSlipNotifications();
      }
    }
  };

  pendingRequestCount = 0;

  processPendingBets = async ({
    pendingBets = [],
    isExpressPresets = false,
    isRestore = false,
    isMixedTurboCombo = false,
    betslipTabName = '',
    betsFromBetslip = [],
  }) => {
    runInAction(() => {
      this.ticketsToRestore = [];
    });

    const setError = () => {
      const { ...newResp } = pendingBets[0];
      const fakeResponse = [{ ...newResp, errorCode: 50, status: 1 }]; //'notProcessed'
      this.processTicketStatuses({
        betsFromBetslip,
        betslipTabName,
        isExpressPresets,
        isMixedTurboCombo,
        isRestore,
        statuses: fakeResponse,
      });
    };

    /**
     * Limit for pending requests to avoid infinite loop in case of server issues or unexpected responses
     * It should be enogh to handle logically posisible delays that can be added on server
     */
    if (this.pendingRequestCount >= 300) {
      setError();
      logger.error('processPendingBets error pendingRequestCount limit', {
        response: pendingBets[0],
      });
      return;
    }

    const promises = pendingBets.map(({ id }) => this.processRequest(id));

    await Promise.all(promises)
      .then((response) => {
        this.pendingRequestCount += 1;
        this.requestCount = 0;
        const responses = response.flatMap((res) => res?.data?.data ?? []);

        this.processTicketStatuses({
          betsFromBetslip,
          betslipTabName,
          isExpressPresets,
          isMixedTurboCombo,
          isRestore,
          statuses: responses,
        });
      })
      .catch(async (response) => {
        if (response === 'Too early request') {
          if (this.requestCount < 10) {
            await timeout(0.2);
            this.requestCount = this.requestCount++;

            await this.processPendingBets({
              isExpressPresets,
              isMixedTurboCombo,
              isRestore,
              pendingBets,
            });
          } else {
            this.requestCount = 0;
            this.setInProgress(false);
            this.isExpressPresetsInProgress = false;
            if (!(isExpressPresets || isMixedTurboCombo)) {
              this.clearBets();
            }
            setError();
            logger.error('processPendingBets error requestCount limit', {
              response,
            });
          }
        } else {
          this.setInProgress(false);
          this.isExpressPresetsInProgress = false;
          if (!(isExpressPresets || isMixedTurboCombo)) {
            this.clearBets();
          }
          setError();
        }
        logger.error('processPendingBets error', {
          response,
        });
      });
  };

  processRequest = (id) => getBetStatus({ id });

  pushBetsToAnalytics = ({
    successAndFiledStatuses,
    failedStatuses,
    betsFromBetslip,
    isInitialProcess,
    isExpressPresets,
    isMixedTurboCombo,
    betslipTabName,
  }) => {
    const getBetsByIds = (betIds) => {
      return betIds.reduce((acc, betId) => {
        const { outcomeId } = betId || {};
        const bet = betsFromBetslip.find(({ id }) => id === outcomeId);

        if (bet) {
          acc.push(bet);
        }
        return acc;
      }, []);
    };

    const betTypeForAnalytics =
      isExpressPresets || isMixedTurboCombo
        ? isMixedTurboCombo
          ? 'turbo_combo_bet_slip'
          : 'turbo_combo'
        : 'default';

    if (isInitialProcess) {
      analytics.pushBetInfo({
        action: dataLayerActions.BEGIN_CHECKOUT,
        bets: betsFromBetslip,
        betslipTabName,
        currentBetType: betTypeForAnalytics,
      });
    }

    successAndFiledStatuses.forEach((respond) => {
      const { bets: betIds, id: transactionId } = respond;

      analytics.pushBetInfo({
        action: dataLayerActions.PLACE_BET,
        bets: getBetsByIds(betIds),
        betslipTabName,
        currentBetType: betTypeForAnalytics,
        transactionId,
      });
    });

    failedStatuses.forEach((respond) => {
      const { bets: betIds, id: transactionId, errorCode } = respond;

      analytics.pushBetInfo({
        action: dataLayerActions.REFUND,
        bets: getBetsByIds(betIds),
        currentBetType: betTypeForAnalytics,
        errorCode,
        transactionId,
      });
    });
  };

  processTicketStatuses = async ({
    statuses = [],
    isInitial = false,
    isExpressPresets = false,
    isRestore = false,
    isMixedTurboCombo = false,
    betslipTabName = '',
    betsFromBetslip = [],
  }) => {
    const minDuration = 1.5;
    const onDoneDelay = 1;
    const successAndPendingStatusCodes = [0, 2];

    const {
      failed: failedStatusResponds,
      pending: pendingStatusResponds,
      success: successStatusResponds,
      successAndFiled: successAndFiledStatuses,
    } = statuses.reduce(
      (acc, respond) => {
        const { status } = respond;

        if (successAndPendingStatusCodes.includes(status)) {
          switch (status) {
            case 0:
              acc.pending.push(respond);
              break;
            case 2:
              acc.successAndFiled.push(respond);
              acc.success.push(respond);
              this.emitCefChangedAlert(respond.coefficient, respond.placebetCoefficient);
              break;
          }
        } else {
          acc.successAndFiled.push(respond);
          acc.failed.push(respond);
        }
        return acc;
      },
      {
        failed: [],
        pending: [],
        success: [],
        successAndFiled: [],
      },
    );

    this.pushBetsToAnalytics({
      betsFromBetslip,
      betslipTabName,
      failedStatuses: failedStatusResponds,
      isExpressPresets,
      isInitialProcess: isInitial,
      isMixedTurboCombo,
      successAndFiledStatuses,
    });

    if (isInitial) {
      this.statusRespondsLength = statuses.length;
      this.isPendingBets = true;
    }

    this.failedStatusesLength += failedStatusResponds.length;
    this.successStatusesLength += successStatusResponds.length;

    if (successStatusResponds.length) {
      if (!(pendingStatusResponds.length || this.maxDelay)) {
        this.setMaxDelay(minDuration);
        await timeout(minDuration);
        this.setIsPendingBetsFinished(true);

        if (!(isExpressPresets || isMixedTurboCombo)) {
          await timeout(onDoneDelay);

          successStatusResponds.forEach(({ bets }) => {
            bets.forEach(({ outcomeId }) => {
              this.removeSuccessfulBet(outcomeId);
            });
          });
        }
      }
    }

    if (pendingStatusResponds.length) {
      const delayRequest = pendingStatusResponds.reduce((prev, { delay, id, bets }) => {
        if (!(isExpressPresets || isMixedTurboCombo)) {
          bets.forEach(({ outcomeId = '' }) => {
            this.addTicketIdToStorageBet(outcomeId, id);
          });
        }
        return prev > delay ? prev : delay;
      }, 0);
      this.setMaxDelay(delayRequest);
      await timeout(delayRequest);
    }

    this.pendingBets = [...this.pendingBets, ...successStatusResponds.map(({ id }) => id)];
    this.pendingBets.forEach((id) => {
      this.pendingBets = this.pendingBets.filter((i) => id !== i);

      const item = statuses.find(({ id: sId }) => sId === id);
      const { bets, status, errorCode } = item;

      bets.forEach(({ outcomeId }) => {
        if (outcomeId) {
          this.betStatuses.push({
            errorCode,
            errors: [],
            id: outcomeId,
            status,
          });
        }
      });
    });

    failedStatusResponds.forEach(({ bets, status, errorCode, minBet, id = '' }) => {
      // Sending post message partner if get error code = 2 (Not enough money)
      if (errorCode === 2 && !isRestore) {
        sendMsg('placeBetFailure', {
          status: betStatus.iframeMessage.notEnoughMoney.text,
          statusId: betStatus.iframeMessage.notEnoughMoney.statusId,
        });
      }

      const errorBetStatuses = {
        errorCode,
        ids: [],
        minBet,
        status,
        ticketId: id,
      };

      const esSingleBets = LS.get('esSingleBets') || '[]';
      const items = JSON.parse(esSingleBets);

      bets.forEach(({ outcomeId, error }) => {
        if (outcomeId) {
          const bet = items.find((item) => outcomeId === item.id && 'ticketId' in item);
          if (bet) {
            delete bet.ticketId;
          }

          errorBetStatuses.ids.push(outcomeId);
          this.betStatuses.push({
            errorCode,
            errors: [],
            id: outcomeId,
            isLimitExited: error,
            minBet,
            status,
          });
        }
      });

      LS.set('esSingleBets', JSON.stringify(items));

      this.root.betslipErrors.setErrorBetStatuses(errorBetStatuses);
    });

    if (pendingStatusResponds.length) {
      await this.processPendingBets({
        betsFromBetslip,
        betslipTabName,
        isExpressPresets,
        isMixedTurboCombo,
        pendingBets: pendingStatusResponds,
      });
    } else {
      this.setIsPendingBetsFinished(true);
      this.pendingRequestCount = 0;
      this.setInProgress(false);
      await timeout(onDoneDelay);
      this.updateBetsWithStatuses(isExpressPresets || isMixedTurboCombo);
      this.resetData();

      // if successful bets > 0 and there are no bets with errors, close the mobile coupon
      if (successStatusResponds.length) {
        if (!(isMixedTurboCombo || this.bets.length)) {
          this.root.ui.setBetSlipOpen(false);
        }

        return successStatusResponds;
      }
    }
  };

  processLiveTicketStatuses = async (ticket = {}, initial = false) => {
    const { status, id = '', bets = [], delay, errorCode } = ticket;
    const minDuration = 1.5;
    const onDoneDelay = 1;
    const betType = 'live_bet';
    this.statusRespondsLength = 1;
    this.isPendingBets = true;

    if (initial) {
      analytics.pushBetInfo({
        action: dataLayerActions.BEGIN_CHECKOUT,
        bets: [this.liveBet],
        currentBetType: betType,
      });
    }
    if (status !== 0) {
      analytics.pushBetInfo({
        action: dataLayerActions.PLACE_BET,
        bets: [this.liveBet],
        currentBetType: betType,
        transactionId: id,
      });
    }

    const [liveBetRespond] = bets;
    switch (status) {
      case 0: {
        // Pending
        const delayRequest = Math.max(delay, 2);
        this.setMaxDelay(delayRequest);
        await timeout(delayRequest);

        const { data: { data: resopnse } = {} } = await this.processRequest(id);

        this.processLiveTicketStatuses(resopnse);

        break;
      }

      case 2: {
        // Success
        this.successStatusesLength = 1;

        this.emitCefChangedAlert(ticket.coefficient, ticket.placebetCoefficient);

        if (initial) {
          this.setMaxDelay(minDuration);
          await timeout(onDoneDelay);
        }
        this.setIsPendingBetsFinished(true);
        await timeout(onDoneDelay);

        if (this.liveBet.id === liveBetRespond.outcomeId) {
          bets.forEach(({ outcomeId }) => {
            if (outcomeId) {
              this.betStatuses.push({
                errorCode,
                errors: [],
                id: outcomeId,
                status,
              });
            }
          });

          this.setLiveBet({});
          this.setInProgress(false);
        }

        //Updating Balance on successfull live bet
        this.root.profile?.updateBalance();

        // FIX immideatly removing of betStatuses (in live mode no succes notification)
        if (this.root.ui.liveView) break;
        this.root.ui.setBetSlipOpen(false);
        break;
      }

      default:
        // FAILED
        this.failedStatusesLength = 1;

        analytics.pushBetInfo({
          action: dataLayerActions.REFUND,
          bets: [this.liveBet],
          currentBetType: betType,
          errorCode,
          transactionId: id,
        });

        // Sending post message partner if get error code = 2 (Not enough money)
        if (errorCode === 2) {
          sendMsg('placeBetFailure', {
            status: betStatus.iframeMessage.notEnoughMoney.text,
            statusId: betStatus.iframeMessage.notEnoughMoney.statusId,
          });
        }

        bets.forEach(({ outcomeId }) => {
          if (outcomeId) {
            this.betStatuses.push({
              errorCode,
              errors: [],
              id: outcomeId,
              status,
              ticketId: id,
            });
          }
        });

        this.setInProgress(false);

        break;
    }
    if (status !== 0) {
      setTimeout(() => {
        this.statusRespondsLength = 0;
        this.failedStatusesLength = 0;
        this.successStatusesLength = 0;
        this.isPendingBets = false;
        this.setIsPendingBetsFinished(false);
      }, onDoneDelay);
    }
  };

  updateBetsWithStatuses = (isExpressPresets = false) => {
    if (!isExpressPresets) {
      const idsToRemove = this.bets.reduce((acc, betItem) => {
        const bet = this.betStatuses.find(({ id }) => id === betItem.id);
        if (bet && bet.status === 2) {
          this.root.store.updateOutcomeById(bet.id, bet.pidLid, 'isActive', false);
          acc.push(bet.id);
        }
        return acc;
      }, []);

      if (idsToRemove.length > 0) {
        idsToRemove.forEach((id) => {
          this.removeSuccessfulBet(id);
        });
      }
    }

    if (this.pendingBets.length === 0) {
      this.setInProgress(false);

      this.statusRespondsLength = 0;
      this.failedStatusesLength = 0;
      this.successStatusesLength = 0;
      this.isPendingBets = false;
      this.setIsPendingBetsFinished(false);
      this.isExpressPresetsInProgress = false;

      if (this.bets.length === 0 && !isExpressPresets) {
        this.clearStorage();
        LS.remove('esBetslipTab');
        if (!isEmpty(this.root.betBuilder.betBuilderData)) {
          this.root.betBuilder.betBuilderData = {};
        }
      }
    }
    //Updating Balance on successfull bet
    const { profile } = this.root;
    profile.updateBalance();

    if (isExpressPresets) return;
    this.getBonuses();
  };

  /**
   * Initializes a new Bet instance with the provided identifiers and track mark.
   * @param {Object} params - Parameters for initializing a new bet.
   * @param {string} params.id - The outcome identifier.
   * @param {string} params.marketId - The market identifier.
   * @param {(string|string[])} params.trackMarks - The track mark or an array of track marks.
   * @returns {Bet} A new Bet instance configured with the given parameters.
   */
  initNewBet = ({ id, marketId, trackMarks }) => {
    const {
      root: { store, profile },
    } = this;

    let outcome = store.getOutcomeByLID(id, marketId);
    if (outcome) {
      const currentTrackMark = Array.isArray(trackMarks) ? trackMarks : [trackMarks];
      const { pid: marketLid, price } = outcome;
      const market = store.markets.get(marketLid);
      const { pid: eventLid, name, lid } = market;
      const event = store.events.get(eventLid);
      const { startTime, slug, did, name: eventName = '', pid = '' } = event || {};
      const tournamentId = pid.split('.').pop();

      let stake = this.defaultStake;
      if (currentTrackMark.includes(betTrackMark.popularBetsWidget)) {
        // if bet was added from popular bets widget, then take stake from popular bets
        const currentPopularBet = this.popularBets.find(({ outcomeId }) => outcomeId === id);
        if (currentPopularBet) {
          stake = profile.roundCurrency(currentPopularBet.avgAmountByDiscipline) || stake;
        }
      }

      outcome = {
        ...outcome,
        betHistory: [{ price, timestamp: new Date().getTime() }],
        disciplineId: did,
        eventName,
        marketName: name,
        pidLid: lid,
        slug,
        stake,
        startTime,
        tournamentId,
        trackMarks: currentTrackMark,
      };
    }
    return new Bet(outcome, store, this);
  };

  initNewExpressPresetsBet = (expressPresetOutcome) => {
    const {
      outcomeId,
      pidLid,
      disciplineId,
      eventName,
      eventSlug,
      presetId,
      trackMarks = ['default'],
    } = expressPresetOutcome;

    let outcome = this.root.store.getOutcomeByLID(outcomeId, pidLid);

    if (outcome) {
      const [line, eventId] = pidLid.split('.');
      const { pid: marketLid, price } = outcome;
      const event = this.root.store.events.get(`${line}.${eventId}`) || {};
      const market = this.root.store.markets.get(marketLid);
      const { name: marketName } = market;

      outcome = {
        ...outcome,
        betHistory: [{ price, timestamp: new Date().getTime() }],
        disciplineId,
        event,
        eventName,
        marketName,
        pidLid: marketLid,
        presetId,
        slug: eventSlug,
        stake: this.defaultStake,
        startTime: new Date(expressPresetOutcome.eventDate).getTime() / 1000,
        trackMarks,
      };
    }
    return new Bet(outcome, this.root.store, this);
  };

  getMaxBet = (opts) => {
    return GetMaxBet(opts);
  };

  /**
   * @description - subscribe on markets in socket
   * @param { Object } marketsPids - k - outcome id, v - market pid
   * @param { Boolean } isForcedSubscribe - force subscribe to socket entity even that entity has in store
   * @param { string } widgetType - widget type for logging in Sentry
   * @returns { Promise<{ rejected: Array, resolved: Array}> }
   */
  subscribeOnMarketsInSW = async ({
    marketsPids = {},
    isForcedSubscribe = false,
    widgetType = '',
  }) => {
    const marketsToSubscribe = Object.entries(marketsPids).map(([outcomeId, pidLid]) => {
      if (!isForcedSubscribe) {
        const pureOutcomeId = outcomeId.split('.').pop();
        if (!this.root.store.getOutcomeByLID(pureOutcomeId, pidLid)) {
          return pidLid;
        }
      }
      return pidLid;
    });

    const eventsIds = [
      ...new Set(
        marketsToSubscribe.map((pidLid) => {
          const [line, eventId] = pidLid.split('.');
          return `${line}.${eventId}`;
        }),
      ),
    ];

    try {
      const { rejected: rejectedEvents } = await getFromWebSocket({
        entityName: 'event',
        ids: eventsIds,
      });

      const { rejected: rejectedMarkets, resolved: resolvedMarkets } = await getFromWebSocket({
        entityName: 'market',
        ids: marketsToSubscribe,
      });

      const rejectedEventsThatHasMarkets = new Set();
      const marketsWithEvents = [];
      const marketIdsWithoutEvents = [];
      if (rejectedEvents.length) {
        const rejectedEventsSet = new Set(rejectedEvents);
        resolvedMarkets.forEach((market) => {
          const { pid, lid } = market;
          if (rejectedEventsSet.has(pid)) {
            rejectedEventsThatHasMarkets.add(pid);
            marketIdsWithoutEvents.push(lid);
          } else {
            marketsWithEvents.push(market);
          }
        });

        if (rejectedEventsThatHasMarkets.size && widgetType) {
          logger.error('Error from EN. Got rejected events that has markets', {
            eventIds: Array.from(rejectedEventsThatHasMarkets),
            widgetType,
          });
        }
      }

      return {
        rejected: [...rejectedMarkets, ...marketIdsWithoutEvents],
        resolved: marketsWithEvents,
      };
    } catch (error) {
      logger.error('Error on subscribe on markets in "subscribeOnMarketsInSW" function', { error });
      return { rejected: [], resolved: [] };
    }
  };

  // TODO: This is an MVP for an exhibition. Man up and cut this crap. Do it properly!
  fetchExpressPresets = async ({ disciplineIds, tournamentIds }) => {
    if (this.root.expressPresets.isBetBuilder) {
      const {
        data: { data },
      } = await getBetBuilderRecommendations({
        disciplineIds,
        pageSize: 500, //tmp solution on MVP
      });
      return data;
    }

    const {
      data: { data },
    } = await getExpressRecommendations({
      disciplineIds,
      tournamentIds,
    });
    return data;
  };

  getExpressPresets = async (disciplineIds, tournamentIds, setIsLoading = () => {}) => {
    const getPresetId = (preset) => {
      const eventMarketsIds = preset.reduce((acc, { eventMarketId }) => acc + eventMarketId, '');
      return getHashFromString(eventMarketsIds);
    };

    const presetsLoadingId = disciplineIds.toString();
    if (presetsLoadingId === this.expressPresetsLoadingId) {
      setIsLoading(true, true);
      return;
    }

    const isExpressPresetsByTournament = !!tournamentIds.length;

    try {
      if (isExpressPresetsByTournament) {
        this.setIsUpdateExpressPreset(true);
      }
      setIsLoading(true, false);
      this.expressPresetsLoadingId = presetsLoadingId;

      const settledPresets = this.handleSettledBetsByLS({ lsKey: 'settledExpressPresets' });
      const outcomesMinNum = this.root.expressPresets.isBetBuilder ? 2 : 4;

      let expressPresets = await this.fetchExpressPresets({
        disciplineIds,
        tournamentIds,
      });

      // TODO: This is an MVP for an exhibition. Man up and cut this crap. Do it properly!
      if (this.root.expressPresets.isBetBuilder) {
        expressPresets = expressPresets.reduce((acc, el) => {
          const preset = [];
          el.outcomes.forEach((outcome) => {
            preset.push({ ...el, ...outcome });
          });
          acc.push(preset);
          return acc;
        }, []);
      }

      const marketsPids = expressPresets.reduce((acc, el) => {
        const presetId = getPresetId(el);
        if (settledPresets[presetId]) {
          return acc;
        }

        el.forEach((outcome) => {
          const pidLid = `1.${outcome.eventId}.${outcome.eventMarketId}`;
          outcome.pidLid = pidLid;
          if (!acc[outcome.outcomeId]) {
            acc[outcome.outcomeId] = pidLid;
          }
        });
        return acc;
      }, {});

      const { rejected: rejectedMarketIds } = await this.subscribeOnMarketsInSW({
        isForcedSubscribe: true,
        marketsPids,
        widgetType: this.root.expressPresets.isBetBuilder
          ? betTrackMark.betBuilderPresetBet
          : betTrackMark.expressPresetBet,
      });
      const rejectedMarketIdsSet = new Set(rejectedMarketIds);

      this.presetsMarketsIds = [...Object.values(marketsPids), ...this.presetsMarketsIds];

      const expressPresetsByDisciplines = expressPresets.reduce((acc, el) => {
        const presetId = getPresetId(el);

        if (settledPresets[presetId]) {
          return acc;
        }

        const preset = el
          .map((outcome) => {
            const { pidLid } = outcome;
            if (!rejectedMarketIdsSet.has(pidLid)) {
              outcome.presetId = presetId;
              return this.initNewExpressPresetsBet(outcome);
            }
          })
          .filter(Boolean);

        if (preset.length < outcomesMinNum) return acc;

        const did = el[0].disciplineId;

        if (acc[did]) {
          acc[did].push(preset);
        } else {
          acc[did] = [preset];
        }
        return acc;
      }, {});

      if (isExpressPresetsByTournament) {
        this.expressPresetsByTournament = expressPresetsByDisciplines;
      } else {
        this.expressPresets = { ...this.expressPresets, ...expressPresetsByDisciplines };
      }

      setIsLoading(false, true);
      this.expressPresetsLoadingId = '';
      await timeout(2);
      this.setIsUpdateExpressPreset(false);
    } catch (error) {
      logger.error('getExpressPresets error', {
        error,
      });
      setIsLoading(false, true);
      this.expressPresetsLoadingId = '';
    }
  };

  getPresetSum = (opts) => {
    return getPresetSum(opts);
  };

  setPresetSumData = ({ outcomeId = '', data = [], isRemove = false, isClear = false }) => {
    switch (true) {
      case isClear:
        this.presetSumData = {
          quickBetsPreset: this.quickBetsPresetSum,
        };
        break;
      case isRemove:
        delete this.presetSumData[outcomeId];
        this.presetSumData = {
          quickBetsPreset: this.quickBetsPresetSum,
        };
        break;
      default:
        this.presetSumData[outcomeId] = data;
    }
  };

  prepareBet = (bet, isExpress = false) => {
    const {
      id,
      pidLid,
      stake,
      bonusId,
      maxBet = null,
      isBetBuilder = false,
      betBuilderOutcomes = [],
      coefficient,
      trackMarks,
      eventId,
    } = bet;
    const {
      profile: {
        currency: { name },
      },
      store: { getOutcomeByLID },
    } = this.root;

    if (isBetBuilder) {
      return betBuilderOutcomes.map(({ outcomeId = '' }) => {
        return {
          coefficient,
          eventId,
          outcomeId,
          trackMarks,
        };
      });
    }
    const outcomeItem = getOutcomeByLID(id, pidLid);
    if (outcomeItem) {
      const result = {
        coefficient: parseFloat(outcomeItem.price),
        eventId,
        outcomeId: id,
        trackMarks,
      };
      if (!isExpress) {
        const amount = parseFloat(stake);
        if (!amount || amount <= 0) return null;
        result.amount = amount;
        result.currency = name;
        result.maxBet = maxBet;
        if (bonusId) {
          result.bonusId = bonusId;
        }
      }
      return result;
    } else {
      return null;
    }
  };

  clearBets = async () => {
    this.isClearBets = true;
    await this.clearStorage();
    const ids = this.bets.map(({ id }) => id);
    await ids.forEach((id) => {
      this.remove(id);
    });
    this.isClearBets = false;
  };

  getDefaultStake = (bets = []) => {
    if (!bets.length) {
      return;
    }
    try {
      const { stake } = bets[bets.length - 1];
      this.setDefaultStake(stake);
    } catch (error) {
      logger.error('getDefaultStake error', {
        error,
      });
    }
  };

  setDefaultStake = (stake) => {
    const currentDefaultStake = stake ?? LS.get('defaultStake') ?? null;
    this.quickBetsSum = currentDefaultStake;
    if (this.root.ui.presetLastBetSum) {
      this.defaultStake = currentDefaultStake;
      this.expressAmount = currentDefaultStake;
    } else {
      this.defaultStake = null;
      this.expressAmount = null;
    }
    LS.set('defaultStake', currentDefaultStake);
  };

  updateInitialBet = ({ id, price }) => {
    const bets = this.initialBets.filter((bet) => bet.id !== id);
    this.initialBets = [...bets, { id, price }];
  };
  removeInitialBet = (id) => {
    this.initialBets = this.initialBets.filter((bet) => bet.id !== id);
  };
  clearInitialBets = () => {
    this.initialBets = [];
  };
  actualizeInitialBets = () => {
    this.bets.forEach((bet) => {
      this.updateInitialBet(bet);
    });
  };

  setBetItemWithMaxBet = (bool = false) => {
    this.betItemWithMaxBet = bool;
  };

  setBonusBetInProgressId = (id = null) => {
    this.bonusBetInProgressId = id;
  };

  setIsExpressPresetBetSlip = (bool = false) => {
    this.isExpressPresetBetSlip = bool;
  };

  setMaxDelay = (num = null) => {
    this.maxDelay = num;
  };

  setIsDisabledPlacebetButton = (bool = false) => {
    this.isDisabledPlacebetButton = bool;
  };

  setIsPendingBetsFinished = (bool = false) => {
    this.isPendingBetsFinished = bool;
  };

  setIsBetslipSlots = (bool = false) => {
    this.isBetslipSlots = bool;
  };

  checkBetSlipWithBonus = () => {
    if (this.currentTab === 'SINGLEBET') {
      this.isBetSlipWithBonuses = this.betsWithBonus.some(
        ({ bonus: { minSelection = 0 } }) => minSelection === 1,
      );
    }
    if (this.currentTab === 'EXPRESS') {
      this.isBetSlipWithBonuses = this.hasExpressBonus;
    }
  };

  // ? This alert triggers when the bet is accepted with a different coefficient
  // ? than the one that was at the time of the bet was placed.
  emitCefChangedAlert = (coefficient, placebetCoefficient) => {
    if (coefficient !== placebetCoefficient) {
      this.root.ui.setAlert({
        text: placebetCoefficient,
        timeout: this.root.ui.isMobile ? 5000 : 3000,
        title: 'BETSLIP.COEFFICIENT_CHANGED',
        type: 'warning',
      });
    }
  };

  /* Helpers */

  /**
   * @param {Object} bet - The bet to validate
   * @returns {boolean} - Whether the bet amount is valid
   */
  isValidBetAmount = (bet) => {
    if (!bet) return false;
    const pick = (v) => {
      if (typeof v === 'number') return v;
      if (typeof v === 'string') {
        const n = parseFloat(v.replace(',', '.'));
        return Number.isFinite(n) ? n : null;
      }
      return null;
    };
    const amount = pick(bet.amount ?? bet.stake);
    return amount !== null && amount > 0;
  };
}
